import React, { ChangeEvent, useEffect, useRef, useState, KeyboardEvent, useCallback } from "react";
import { FieldArray, FieldArrayRenderProps, FormikProps } from "formik";
import TextField from "../../../../../shared/components/forms/TextField";
import Button from "../../../../../shared/components/general/Button";
import styled from "@emotion/styled";
import { css } from "@emotion/css";
import StyledDropdown from "../../../../../shared/scenes/applicationSettings/components/customAssistantConfigurations/StyledDropdown";
import { getAIAnsweringModuleId, getFoodOrderingModuleId, getHealthcareSchedulingModuleId } from "../../../../../shared/constants/ModuleConsts";
import { getAvailableTemplateConfigurations } from "../../../../../shared/api/controllers/templateConfigurations";
import { getPOSConfig, updateApplication } from "../../../../../shared/api/controllers/applications";
import { getAppliedApplicationTemplatesForApp } from "../../../../../shared/api/controllers/appliedApplicationTemplates";
import { AIAnsweringFeatureFlagId, FoodOrderingFeatureFlagId, HealthcareSchedulingFeatureFlagId, OutboundCallFeatureFlagId } from "../../../../../shared/constants/featureFlagIds";
import { getDefaultFormFieldsByAssistantType, getDefaultFormFieldsByPosName, getSandboxExtraFormFieldsByPosName } from "../../../../../shared/constants/sparkConstants/defaultFormFieldsByAssistantType";
import { SANDBOX_LOCATION_IDS } from "../../../../../shared/constants/sparkConstants/defaultSparkAssistantConfigurations";
import {
    color_shades_darker,
    color_shades_dark,
    color_shades_light,
    color_shades_lightest,
    silver_two,
    text_blue,
    dark_grey_blue,
    color_shades_darkest,
    color_colors_decline,
    color_variants_decline_light_1,
    pale_grey,
    cool_grey,
} from "../../../../../shared/constants/colors";
import CheckboxField from "../../../../../shared/components/forms/CheckboxField";
import ApplicationModel from "../../../../../shared/models/applications/api/ApplicationModel";
import TemplateFormSectionModel from "../../../../../shared/models/templating/api/TemplateFormSectionModel";
import TemplateFormFieldModel from "../../../../../shared/models/templating/api/TemplateFormFieldModel";
import OrganizationContainer from "../../../../../shared/state/containers/OrganizationContainer";
import ApplicationContainer from "../../../../../shared/state/containers/ApplicationContainer";
import VerticalSeparator from "../../../../../shared/components/structure/VerticalSeparator";
import SearchField from "../../../../../shared/components/forms/SearchField";
import { breakpoint_small } from "../../../../../shared/constants/breakpoints";
import SecondaryLoader from "../../../../../shared/components/general/SecondaryLoader";
import useCSVHandler from "../../../../../shared/hooks/UseCSVHandler";
import { locationNameHeading, storeIdHeading, callDirectionHeading } from "../../../../../shared/constants/sparkConstants/formFieldImportHeadings";
import TooltipWrapper from "../../../../../shared/components/general/TooltipWrapper";
import PageError from "../../../../../shared/components/general/PageError";
import PageWarning from "../../../../../shared/components/general/PageWarning";
import ContentCollapsingSection from "../../../../../shared/components/structure/ContentCollapsingSection";
import { getDefaultAutofillPrompts, FORM_FILL_FIELD_LABELS, FORM_FILL_VARIABLES_BY_ASSISTANT_TYPE } from "../../../../../shared/constants/sparkConstants/formFillFieldConfig";
import AutoGenerateResultsModal from "../../dashboard/autoGenerateFormField/AutoGenerateResultsModal";
import { FieldGenerateResult } from "../../dashboard/autoGenerateFormField/autoGenerateResultUtils";
import { SparkApplicationClientNotesEditor } from "../../../../../shared/components/clientContext/SparkClientContextNotesCard";
import ConfirmationDialog from "../../../../../shared/components/structure/ConfirmationDialog";
import {
    getAvailableRecommendedLocationFields,
    getRecommendedLocationFieldIdentifiers,
    selectRecommendedLocationFields,
    shouldApplyDefaultLocationFields
} from "./locationSpecificRecommendations";

const deleteIcon = require("../../../../../shared/content/images/custom-assistant-deployment/delete.svg");
const xIcon = require("../../../../../shared/content/images/x-small.svg");
const locationsIcon = require("../../../../../shared/content/images/spark/locations-dark.svg");
const checkIcon = require("../../../../../shared/content/images/dark-check.svg");
const searchIcon = require("../../../../../shared/content/images/search-blue.svg");
const pencilIcon = require("../../../../../shared/content/images/pencil-blue-small.svg");
const trashIcon = require("../../../../../shared/content/images/bin-2-gray.svg");
const rightArrow = require("../../../../../shared/content/images/arrow-circle-right.svg");
const lightRightArrow = require("../../../../../shared/content/images/arrow-circle-right-light.svg");
const leftArrow = require("../../../../../shared/content/images/arrow-circle-left.svg");
const lightLeftArrow = require("../../../../../shared/content/images/arrow-circle-left-light.svg");

const fileDownloadIcon = require("../../../../../shared/content/images/menu-item-icons/file-code-download.svg");
const fileUploadIcon = require("../../../../../shared/content/images/menu-item-icons/file-code-upload.svg");
const alertCircleIcon = require("../../../../../shared/content/images/alert-circle.svg");
const autoGenerateIcon = require("../../../../../shared/content/images/spark/auto-generate.svg");
const infoIcon = require("../../../../../shared/content/images/information-circle.svg");

const WAIT_INTERVAL = 400;
const sparkOnboardingCueKey = 'SPARK_ONBOARDING_CUE';

const assistantTypeOptions = [
    { value: 'ai-answering', label: 'AI Answering' },
    { value: 'ai-ordering', label: 'AI Ordering' },
    { value: 'dental-scheduling', label: 'AI Dental Scheduling' }
];

/**
 * Returns true when a template field value is a sentinel string — i.e. the raw
 * associatedVariable name (e.g. "{Webhook-Restaurant-Id}" or "{defaultOfficeId}") stored
 * by the update path to mark a field as location-specific — rather than a real, user-supplied
 * location ID.
 *
 * The same sentinel pattern is guarded against in resolveLocationFieldValue. This helper
 * makes the identical check available to the form's pre-population logic so that sentinel
 * values fetched from applied template form fields are never displayed as real IDs.
 */
export function isLocationIdSentinel(value: string, associatedVariable: string): boolean {
    return value === associatedVariable;
}

export type LocationSubStep =
    | 'pending'
    | 'creating-app'
    | 'updating-app'
    | 'applying-template'
    | 'create-manage-menu'
    | 'pull-menu'
    | 'sync-module'
    | 'provisioning-outbound'
    | 'complete';

export type AutofillFieldStatus = 'pending' | 'fetching' | 'complete' | 'skipped';

export interface AutofillFieldState {
    status: AutofillFieldStatus;
    error?: string;
}

export interface ManageSparkLocationsFormProps {
    appContainer: ApplicationContainer;
    orgContainer: OrganizationContainer;
    commonApp: ApplicationModel;
    locationApps: ApplicationModel[];
    manageMenuApps?: ApplicationModel[];
    locationAppNames: string[];
    locationAppFormFields: TemplateFormFieldModel[];
    commonAppSections: TemplateFormSectionModel[];
    isLoadingTemplates: boolean;
    history: any;
    isSubmittingForm: boolean;
    setIsSubmittingForm: (isSubmittingForm: boolean) => void;
    progressStep?: string;
    setProgressStep?: (step: string) => void;
    progressLocationName?: string;
    setProgressLocationName?: (name: string) => void;
    progressMode?: 'creating' | 'updating';
    setProgressMode?: (mode: 'creating' | 'updating') => void;
    locationProgress?: Record<string, LocationSubStep>;
    setLocationProgress?: (updater: Record<string, LocationSubStep> | ((prev: Record<string, LocationSubStep>) => Record<string, LocationSubStep>)) => void;
    autofillProgress?: Record<string, AutofillFieldState>;
    setAutofillProgress?: (updater: Record<string, AutofillFieldState> | ((prev: Record<string, AutofillFieldState>) => Record<string, AutofillFieldState>)) => void;
    padding?: string;
}

export interface ManageSparkLocationsFormData {
    commonAppName?: string;
    locationNames: string[];
    selectedFormFields: TemplateFormFieldModel[];
    deselectedFormFields: TemplateFormFieldModel[];
    assistantType: string;
    assistantSetupType: string;     // 'standard' | 'out-of-the-box'
    pmsName?: string;
    // AI Ordering fields
    posNameOrdering?: string;       // POS Name for AI Ordering (separate from pmsName used for Dental Scheduling)
    storeIds?: Record<number, string>; // Per-location Store ID keyed by location index
    websiteUrl?: string; // Org-level website URL used for AI autofill of shared config fields
    autofillPrompts?: Record<string, string>; // Per-variable editable prompts sent as descriptions to the AI formfill endpoint
    locationWebsiteUrls?: Record<number, string>; // Per-location website URL override keyed by location index
    /** Per-location autogenerate flag keyed by location index. True = autogenerate for that location. */
    locationAutogenerateEnabled: Record<number, boolean>;
    /** @deprecated kept for backward-compat; derived as `Object.values(locationAutogenerateEnabled).some(Boolean)` */
    autogenerateEnabled: boolean;
    locationCloneEnabled?: Record<number, boolean>;
    locationCloneSourceAppIds?: Record<number, string>;
    skipPOSConfigLocationIndexes?: number[];
}

export const isSandboxConfiguring = (
    assistantSetupType?: string,
    storeId?: string,
    posNameOrdering?: string
): boolean => {
    if (assistantSetupType === 'out-of-the-box') return true;
    if (!storeId) return false;
    const trimmed = storeId.trim().toLowerCase();
    const sandboxIds = Object.values(SANDBOX_LOCATION_IDS).map(id => id.toLowerCase());
    if (sandboxIds.includes(trimmed)) return true;
    if (posNameOrdering && SANDBOX_LOCATION_IDS[posNameOrdering]?.toLowerCase() === trimmed) return true;
    return false;
};

export const loadPOSConfigForLocationId = (
    locationId: string,
    loader: typeof getPOSConfig = getPOSConfig
) => {
    return loader(locationId.trim());
};

interface ExistingPOSConfigConflict {
    locationIndex: number;
    locationName: string;
    storeId: string;
}

const SparkLocationsForm: React.FC<ManageSparkLocationsFormProps & FormikProps<ManageSparkLocationsFormData>> = (props) => {

    const [activeIndex, setActiveIndex] = useState(0);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const normalizeUrl = (value: string): string => {
        const trimmed = value.trim();
        if (!trimmed) return trimmed;
        if (/^https?:\/\//i.test(trimmed)) return trimmed;
        return `https://${trimmed}`;
    };
    const [isLocationCollapsed, setIsLocationCollapsed] = useState(false);
    const [search, setSearch] = useState("");
    const [filteredSections, setFilteredSections] = useState(props.commonAppSections);
    const allSectionsRef = useRef<TemplateFormSectionModel[]>(props.commonAppSections);
    const [originalSelectedFormFields, setOriginalSelectedFormFields] = useState<TemplateFormFieldModel[]>();
    const hasPopulatedFormFieldsRef = useRef(false);
    const [isAssistantTypeDisabled, setIsAssistantTypeDisabled] = useState(false);
    const [submissionErrors, setSubmissionErrors] = useState<string[]>([]);
    const [submissionSuccess, setSubmissionSuccess] = useState(false);
    const [showAutofillResultsModal, setShowAutofillResultsModal] = useState(false);
    const [autofillSuccessFields, setAutofillSuccessFields] = useState<FieldGenerateResult[]>([]);
    const [autofillFailedFields, setAutofillFailedFields] = useState<FieldGenerateResult[]>([]);
    const [showCheckboxes, setShowCheckboxes] = useState(true);
    const [showClientNotesModal, setShowClientNotesModal] = useState(false);
    const [isCheckingPOSConfigs, setIsCheckingPOSConfigs] = useState(false);
    const [existingPOSConfigConflicts, setExistingPOSConfigConflicts] = useState<ExistingPOSConfigConflict[]>([]);

    // App name editing state
    const [editingAppId, setEditingAppId] = useState<string | null>(null);
    const [editingAppName, setEditingAppName] = useState<string>("");
    const [isSavingAppName, setIsSavingAppName] = useState(false);
    const [appNameSaveError, setAppNameSaveError] = useState<string | null>(null);
    // Local overrides for app names after a successful rename (appId -> new name)
    const [localAppNames, setLocalAppNames] = useState<Record<string, string>>({});

    // CSV import/export
    const { importedData, importCSV, exportCSV } = useCSVHandler();
    const csvImportInputRef = useRef<HTMLInputElement>(null);
    const locationTopicsRef = useRef<HTMLDivElement>(null);
    const posNameRef = useRef<HTMLDivElement>(null);
    const locationNamesRef = useRef<HTMLDivElement>(null);
    const stepperRef = useRef<HTMLDivElement>(null);
    const environmentRef = useRef<HTMLDivElement>(null);
    const autogenerateRowRef = useRef<HTMLDivElement>(null);
    const websiteUrlInputWrapperRef = useRef<HTMLDivElement>(null);
    const formRef = useRef<HTMLFormElement>(null);

    const recommendedLocationFieldIdentifiers = getRecommendedLocationFieldIdentifiers(
        props.values.assistantType,
        props.values.posNameOrdering
    );
    const availableRecommendedLocationFields = getAvailableRecommendedLocationFields(
        allSectionsRef.current,
        recommendedLocationFieldIdentifiers
    );
    const selectedFieldsWithRecommendations = selectRecommendedLocationFields(
        props.values.selectedFormFields,
        availableRecommendedLocationFields
    );
    const allRecommendedLocationFieldsSelected = availableRecommendedLocationFields.length > 0
        && selectedFieldsWithRecommendations.length === props.values.selectedFormFields.length;

    const handleSelectRecommendedLocationFields = () => {
        if (allRecommendedLocationFieldsSelected || availableRecommendedLocationFields.length === 0) {
            return;
        }

        props.setFieldValue("selectedFormFields", selectedFieldsWithRecommendations);
    };

    // When Environment is selected on initial config and any location has autogenerate enabled,
    // focus the Organization Website URL input.
    useEffect(() => {
        const anyLocationAutogenerate = Object.values(props.values.locationAutogenerateEnabled ?? {}).some(Boolean);
        if (!props.commonApp && props.values.assistantSetupType && anyLocationAutogenerate) {
            setTimeout(() => {
                const input = websiteUrlInputWrapperRef.current?.querySelector('input');
                input?.focus();
            }, 100);
        }
    }, [props.values.assistantSetupType]);

    const processLocationCSV = (csvArray: string[][]): { [key: string]: string }[] => {
        // Skip header row (index 0)
        return csvArray.slice(1)
            .filter(row => row.length >= 1 && row[0]?.trim())
            .map(row => {
                return {
                    [locationNameHeading]: row[0]?.trim() ?? '',
                    [storeIdHeading]: row[1]?.trim() ?? '',
                };
            });
    };

    const handleCSVImport = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            importCSV(file, processLocationCSV);
        }
        // Reset the input so the same file can be re-imported if needed
        e.target.value = '';
    };

    const downloadLocationTemplate = () => {
        exportCSV(
            [{ [locationNameHeading]: '', [storeIdHeading]: '' }],
            [locationNameHeading, storeIdHeading],
            'location-import-template'
        );
    };

    const searchTimer = useRef<NodeJS.Timer>(null);

    const pmsNameOptions = [
        { value: 'cloud-9', label: 'Cloud 9' },
        { value: 'denticon', label: 'Denticon' },
        { value: 'dentrix-ascend', label: 'Dentrix Ascend' },
        { value: 'open-dental', label: 'Open Dental' }
    ];

    // POS Name options for AI Ordering — values must match the POS name constants used by the ordering backend
    const posNameOrderingOptions = [
        { value: 'ADORA', label: 'Adora' },
        { value: 'DELIVERPLUS', label: 'Deliver Plus' },
        { value: 'OLO', label: 'Olo' },
        { value: 'PAR', label: 'Par' },
        { value: 'PAYTRONIX', label: 'Paytronix' },
        { value: 'SPEEDLINE', label: 'Speedline' },
        { value: 'SQUARE', label: 'Square' },
        { value: 'TOAST', label: 'Toast' },
    ];

    const assistantSetupTypeOptions = [
        { value: 'standard', label: 'Production' },
        { value: 'out-of-the-box', label: 'Voicify Sandbox' },
    ];

    // Function to determine assistant type from existing apps
    const determineAssistantTypeFromApps = () => {
        // If there's a common app present, disable the dropdown
        if (props.commonApp) {
            setIsAssistantTypeDisabled(true);

            // Check the common app for feature flags to determine type
            if (props.commonApp.featureFlags) {
                const featureFlags = props.commonApp.featureFlags;

                if (featureFlags.some(flag => flag.id === HealthcareSchedulingFeatureFlagId)) {
                    return 'dental-scheduling';
                } else if (featureFlags.some(flag => flag.id === FoodOrderingFeatureFlagId)) {
                    return 'ai-ordering';
                } else if (featureFlags.some(flag => flag.id === AIAnsweringFeatureFlagId)) {
                    return 'ai-answering';
                }
            }

            // Common app exists but no matching feature flags found
            return null;
        }

        // No common app, dropdown should be enabled
        setIsAssistantTypeDisabled(false);
        return null;
    };

    // Function to get sections based on selected assistant type
    const getSectionsForAssistantType = async (assistantType: string) => {
        if (!assistantType) return [];

        try {
            const availableTemplateConfigsResponse = await getAvailableTemplateConfigurations(props.orgContainer.state.currentOrganization.id);

            let selectedModuleId: string;
            switch (assistantType) {
                case 'ai-answering':
                    selectedModuleId = getAIAnsweringModuleId();
                    break;
                case 'ai-ordering':
                    selectedModuleId = getFoodOrderingModuleId();
                    break;
                case 'dental-scheduling':
                    selectedModuleId = getHealthcareSchedulingModuleId();
                    break;
                default:
                    selectedModuleId = getAIAnsweringModuleId();
            }

            const selectedModuleConfig = availableTemplateConfigsResponse.data.find(config => config.id === selectedModuleId);
            const sections = selectedModuleConfig?.templateForm?.templateFormSections || [];

            return sections;
        } catch (error) {
            console.error('Error loading sections for assistant type:', error);
            return [];
        }
    };

    useEffect(() => {
        setOriginalSelectedFormFields(props.values.selectedFormFields);
    }, [])

    // Effect to determine assistant type based on existing apps
    useEffect(() => {
        const determinedType = determineAssistantTypeFromApps();
        if (determinedType && !props.values.assistantType) {
            props.setFieldValue("assistantType", determinedType);
        }
    }, [props.locationApps, props.commonApp])

    // Update filtered sections when assistant type changes
    useEffect(() => {
        const updateSectionsForAssistantType = async () => {
            if (props.values.assistantType) {
                const newSections = await getSectionsForAssistantType(props.values.assistantType);
                allSectionsRef.current = newSections;
                setFilteredSections(newSections);

                // Apply default form fields for the selected assistant type if:
                // 1. There are no existing location app form fields (new assistant being created)
                // 2. The selected form fields are currently empty
                const shouldApplyDefaults = shouldApplyDefaultLocationFields(
                    props.locationAppFormFields.length,
                    props.values.selectedFormFields.length,
                    !!props.commonApp
                );

                if (shouldApplyDefaults) {
                    const defaultFieldTitles = getDefaultFormFieldsByAssistantType(props.values.assistantType);

                    // Find matching form fields from the sections
                    const allFormFields = newSections.flatMap(section => section.templateFormFields);
                    const defaultFormFields = allFormFields.filter(field =>
                        (field.associatedVariable && defaultFieldTitles.includes(field.associatedVariable))
                        || defaultFieldTitles.includes(field.title)
                    );

                    if (defaultFormFields.length > 0) {
                        props.setFieldValue("selectedFormFields", defaultFormFields);
                    }
                }

                setActiveIndex(0);
            } else {
                setFilteredSections([]);
            }
        };

        updateSectionsForAssistantType();
    }, [props.values.assistantType]);

    // When the POS name changes for AI Ordering, apply POS-specific default form fields
    useEffect(() => {
        if (props.values.assistantType !== 'ai-ordering' || !props.values.posNameOrdering) return;

        const shouldApplyDefaults = shouldApplyDefaultLocationFields(
            props.locationAppFormFields.length,
            props.values.selectedFormFields.length,
            !!props.commonApp
        );

        if (shouldApplyDefaults) {
            const defaultFieldTitles = getDefaultFormFieldsByPosName(props.values.posNameOrdering);
            if (defaultFieldTitles.length === 0) return;

            const allFormFields = filteredSections.flatMap(section => section.templateFormFields);
            const defaultFormFields = allFormFields.filter(field =>
                (field.associatedVariable && defaultFieldTitles.includes(field.associatedVariable))
                || defaultFieldTitles.includes(field.title)
            );
            if (defaultFormFields.length > 0) {
                props.setFieldValue("selectedFormFields", defaultFormFields);
            }
        }
    }, [props.values.posNameOrdering]);

    // When Environment or PMS Name changes for Dental Scheduling, pre-populate empty
    // sandbox Location IDs. Never replace an ID the user entered in the Configuration tab.
    useEffect(() => {
        if (props.values.assistantSetupType !== 'out-of-the-box') return;
        if (props.values.assistantType !== 'dental-scheduling') return;
        if (!props.values.pmsName) return;

        const dentalSandboxLocationIds: Record<string, string> = {
            'denticon': '266',
            'dentrix-ascend': '7000000000392',
        };
        const sandboxLocationId = dentalSandboxLocationIds[props.values.pmsName];
        const updatedStoreIds: Record<number, string> = { ...(props.values.storeIds ?? {}) };
        props.values.locationNames.forEach((_, index) => {
            if (!updatedStoreIds[index]) {
                updatedStoreIds[index] = sandboxLocationId ?? '';
            }
        });
        props.setFieldValue('storeIds', updatedStoreIds);
    }, [props.values.assistantSetupType, props.values.pmsName]);

    // When Environment or POS name changes, pre-populate empty sandbox Location IDs
    // and check any sandbox-only extra fields (e.g. Special Dates & Holidays, Time Zone).
    // Never replace a Store ID the user entered in the Configuration tab.
    useEffect(() => {
        if (props.values.assistantSetupType !== 'out-of-the-box') return;
        if (props.values.assistantType !== 'ai-ordering') return;
        if (!props.values.posNameOrdering) return;

        const sandboxLocationId = SANDBOX_LOCATION_IDS[props.values.posNameOrdering];
        const updatedStoreIds: Record<number, string> = { ...(props.values.storeIds ?? {}) };
        props.values.locationNames.forEach((_, index) => {
            if (!updatedStoreIds[index]) {
                updatedStoreIds[index] = sandboxLocationId ?? '';
            }
        });
        props.setFieldValue('storeIds', updatedStoreIds);

        // Add sandbox-specific extra fields to selectedFormFields if not already selected
        const extraFieldTitles = getSandboxExtraFormFieldsByPosName(props.values.posNameOrdering);
        if (extraFieldTitles.length > 0) {
            const allFormFields = filteredSections.flatMap(section => section.templateFormFields);
            const extraFields = allFormFields.filter(
                field => {
                    const matches =
                        (field.associatedVariable && extraFieldTitles.includes(field.associatedVariable))
                        || extraFieldTitles.includes(field.title);
                    return matches && !props.values.selectedFormFields.some(sf => sf.id === field.id);
                }
            );
            if (extraFields.length > 0) {
                props.setFieldValue('selectedFormFields', [...props.values.selectedFormFields, ...extraFields]);
            }
        }
    }, [props.values.assistantSetupType, props.values.posNameOrdering]);

    useEffect(() => {
        // set initial selectedFormFields value, since it's not set in time by the mapPropsToValues Formik function
        if (props.commonApp) {
            props.setFieldValue("commonAppName", props.commonApp?.name);
        }
        if (props.locationAppFormFields.length > 0 && !hasPopulatedFormFieldsRef.current) {
            const locationAppFormFieldsCopy = [...props.locationAppFormFields];
            for (const locationAppFormField of locationAppFormFieldsCopy) {
                const commonAppFormField = props.commonAppSections
                    .flatMap((section) => section.templateFormFields)
                    .find((formField) => formField.title === locationAppFormField.title);
                if (commonAppFormField) {
                    locationAppFormField.id = commonAppFormField.id;
                }
            }
            props.setFieldValue("selectedFormFields", locationAppFormFieldsCopy);
            hasPopulatedFormFieldsRef.current = true;
        }
        if (props.locationAppNames.length > 0) {
            props.setFieldValue("locationNames", props.locationAppNames);
        } else {
            props.setFieldValue("locationNames", [""]);
        }
    }, [props.locationAppFormFields, props.locationAppNames, props.commonApp?.name]);

    useEffect(() => {
        // Populate PMS/POS name and per-location store IDs from the existing common/location apps
        if (!props.commonApp) return;

        // Derive assistant type from feature flags first (authoritative), fall back to Formik value.
        // This avoids a race condition where props.values.assistantType hasn't been committed to
        // Formik state yet when this effect first fires.
        const derivedAssistantType = (() => {
            if (props.commonApp.featureFlags?.some(f => f.id === HealthcareSchedulingFeatureFlagId)) return 'dental-scheduling';
            if (props.commonApp.featureFlags?.some(f => f.id === FoodOrderingFeatureFlagId)) return 'ai-ordering';
            return null;
        })() || props.values.assistantType;

        const populateFromExistingApp = async () => {
            // Prefer the app-level pmsName/posName properties. If not present, fall back to reading
            // the saved value from the shared configuration form fields ({PMSName} / {Webhook-Pos-Name}).
            if (props.commonApp?.id) {
                if (derivedAssistantType === 'dental-scheduling') {
                    if (props.commonApp.pmsName) {
                        props.setFieldValue("pmsName", props.commonApp.pmsName);
                    } else {
                        const appliedTemplatesResponse = await getAppliedApplicationTemplatesForApp(props.commonApp.id);
                        const allFieldValues = appliedTemplatesResponse?.data?.flatMap(
                            at => at.templateFormFieldValues ?? []
                        ) ?? [];
                        const pmsFieldValue = allFieldValues.find(fv => fv.associatedVariable === '{PMSName}')?.value;
                        if (pmsFieldValue) {
                            // {PMSName} stores the human-readable label (e.g. "Denticon"). Normalize to the
                            // option value slug (e.g. "denticon") so the dropdown renders the correct selection.
                            const matchedOption = pmsNameOptions.find(
                                o => o.label.toLowerCase() === pmsFieldValue.toLowerCase() || o.value.toLowerCase() === pmsFieldValue.toLowerCase()
                            );
                            props.setFieldValue("pmsName", matchedOption ? matchedOption.value : pmsFieldValue);
                        }
                    }
                } else if (derivedAssistantType === 'ai-ordering') {
                    const firstLocationApp = props.locationApps?.[0];
                    const existingPosName = props.commonApp.posName ?? firstLocationApp?.posName;
                    if (existingPosName) {
                        props.setFieldValue("posNameOrdering", existingPosName);
                    } else {
                        const appliedTemplatesResponse = await getAppliedApplicationTemplatesForApp(props.commonApp.id);
                        const allFieldValues = appliedTemplatesResponse?.data?.flatMap(
                            at => at.templateFormFieldValues ?? []
                        ) ?? [];
                        const posFieldValue = allFieldValues.find(fv => fv.associatedVariable === '{Webhook-Pos-Name}')?.value;
                        if (posFieldValue) {
                            // {Webhook-Pos-Name} stores the POS name constant (e.g. "OLO", "SPEEDLINE").
                            // Normalize to the option value so the dropdown renders the correct selection.
                            const matchedOption = posNameOrderingOptions.find(
                                o => o.label.toLowerCase() === posFieldValue.toLowerCase() || o.value.toLowerCase() === posFieldValue.toLowerCase()
                            );
                            props.setFieldValue("posNameOrdering", matchedOption ? matchedOption.value : posFieldValue);
                        }
                    }
                }
            }

            if (props.locationApps?.length > 0) {
                const newStoreIds: Record<number, string> = {};
                await Promise.all(props.locationApps.map(async (locationApp, index) => {
                    const storeId = derivedAssistantType === 'dental-scheduling'
                        ? locationApp.pmsLocationId
                        : locationApp.posLocationId;
                    if (storeId) {
                        newStoreIds[index] = storeId;
                    } else if (locationApp.id) {
                        // App-level ID not populated — read from the location app's applied template form fields.
                        // Healthcare uses {defaultOfficeId}, AI Ordering uses {Webhook-Restaurant-Id}.
                        const associatedVariable = derivedAssistantType === 'dental-scheduling'
                            ? '{defaultOfficeId}'
                            : '{Webhook-Restaurant-Id}';
                        try {
                            const locationAppliedTemplates = await getAppliedApplicationTemplatesForApp(locationApp.id);
                            const locationFieldValues = locationAppliedTemplates?.data?.flatMap(
                                at => at.templateFormFieldValues ?? []
                            ) ?? [];
                            const fieldValue = locationFieldValues.find(fv => fv.associatedVariable === associatedVariable)?.value;
                            // Guard: reject sentinel strings (e.g. the literal "{Webhook-Restaurant-Id}" or
                            // "{defaultOfficeId}") written by the update path to mark a field as
                            // location-specific. These are not real IDs and must not be displayed as such.
                            if (fieldValue && !isLocationIdSentinel(fieldValue, associatedVariable)) {
                                newStoreIds[index] = fieldValue;
                            }
                        } catch (error) {
                            console.error(
                                "Failed to load applied template field values for Spark location prepopulation.",
                                {
                                    locationAppId: locationApp.id,
                                    associatedVariable,
                                    error,
                                }
                            );
                        }
                    }
                }));
                props.setFieldValue('storeIds', newStoreIds);
            }
        };

        populateFromExistingApp().catch(error =>
            console.error("Failed to populate from existing app for Spark location prepopulation.", error)
        );
    }, [props.commonApp?.id, props.locationApps, props.values.assistantType]);

    useEffect(() => {
        const deselectedFormFields = originalSelectedFormFields?.filter((originalFormField) => {
            return !props.values.selectedFormFields.some((selectedFormField) => selectedFormField.title === originalFormField.title);
        });
        props.setFieldValue("deselectedFormFields", deselectedFormFields);
    }, [props.values.selectedFormFields]);

    // Apply CSV-imported location data into Formik whenever importedData changes
    useEffect(() => {
        if (!importedData || importedData.length === 0) return;

        const newLocationNames: string[] = importedData.map((row: any) => row[locationNameHeading] ?? '');
        const newStoreIds: Record<number, string> = {};
        importedData.forEach((row: any, index: number) => {
            newStoreIds[index] = row[storeIdHeading] ?? '';
        });

        props.setFieldValue('locationNames', newLocationNames);
        props.setFieldValue('storeIds', newStoreIds);
    }, [importedData]);

    // Effect to handle submission results from props or form validation
    useEffect(() => {
        // Check if there are any Formik errors that should be displayed
        if (isSubmitted && !props.isSubmittingForm) {
            // Check for status results (from handleSubmit)
            if (props.status?.type === 'error') {
                const statusErrors = [props.status.message];
                if (props.status.details && props.status.details.length > 0) {
                    statusErrors.push(...props.status.details);
                }
                handleFormSubmissionResult(false, statusErrors);
                return;
            }

            if (props.status?.type === 'success') {
                handleFormSubmissionResult(true);
                return;
            }

            // Check for field validation errors
            const formErrors = Object.keys(props.errors).length > 0 ? Object.values(props.errors) : [];
            if (formErrors.length > 0) {
                handleFormSubmissionResult(false, formErrors as string[]);
            }
            // Don't auto-set success anymore - wait for explicit success status
        }
    }, [isSubmitted, props.isSubmittingForm, props.errors, props.status]);

    // Auto-scroll to the stepper when submission begins
    useEffect(() => {
        if (props.isSubmittingForm) {
            setTimeout(() => {
                scrollToWithOffset(stepperRef.current);
            }, 50);
        }
    }, [props.isSubmittingForm]);

    // On subsequent visits, when a new location is given a URL and autogenerate is enabled,
    // auto-enable the Autogenerate toggle for any location-specific autofill fields that are
    // currently set to Skip (absent from autofillPrompts).
    useEffect(() => {
        // Only applies on subsequent visits (commonApp already exists)
        if (!props.commonApp) return;
        // Only when autogenerate is turned on
        if (!props.values.autogenerateEnabled) return;
        // Only applies to assistant types that support autogenerate
        if (props.values.assistantType !== 'ai-ordering' && props.values.assistantType !== 'dental-scheduling') return;
        // autofillPrompts being undefined means all variables are already active — nothing to restore
        if (props.values.autofillPrompts === undefined) return;

        // Determine how many locations are already saved (existing). Only newly added locations
        // (index >= existingCount) should trigger this behaviour.
        const existingCount = props.locationApps?.length ?? 0;
        const locationUrlValues = props.values.locationWebsiteUrls ?? {};
        const hasNewLocationWithUrl = Object.entries(locationUrlValues).some(
            ([idx, url]) => Number(idx) >= existingCount && (url ?? '').trim().length > 0
        );
        if (!hasNewLocationWithUrl) return;

        // Identify location-specific variables that are currently set to Skip
        const variables = Array.from(
            FORM_FILL_VARIABLES_BY_ASSISTANT_TYPE[props.values.assistantType] ?? []
        );
        const defaultPrompts = getDefaultAutofillPrompts(props.values.assistantType);
        const currentPrompts = props.values.autofillPrompts;

        const variablesToEnable = variables.filter(
            (variable) =>
                props.values.selectedFormFields.some((sf) => sf.associatedVariable === variable) &&
                !(variable in currentPrompts)
        );
        if (variablesToEnable.length === 0) return;

        const updatedPrompts = { ...currentPrompts };
        variablesToEnable.forEach((variable) => {
            updatedPrompts[variable] = defaultPrompts[variable] ?? '';
        });
        props.setFieldValue('autofillPrompts', updatedPrompts);
    }, [
        props.commonApp,
        props.values.autogenerateEnabled,
        props.values.locationWebsiteUrls,
        props.values.selectedFormFields,
        props.values.assistantType,
        // intentionally excludes props.values.autofillPrompts to avoid an infinite update loop
    ]);

    // When initially configuring (no location or shared config apps yet), require PMS Name /
    // System Integration to be selected before subsequent fields become available.
    const isInitialConfiguration = !props.commonApp && (!props.locationApps || props.locationApps.length === 0);
    const initialPrerequisiteComplete =
        !isInitialConfiguration ||
        (props.values.assistantType === 'dental-scheduling' && !!props.values.pmsName) ||
        (props.values.assistantType === 'ai-ordering' && !!props.values.posNameOrdering) ||
        props.values.assistantType === 'ai-answering';

    // Progressive disclosure scroll: when initialPrerequisiteComplete first becomes true,
    // scroll to the Environment dropdown (initial config only).
    const prevInitialPrerequisiteCompleteRef = useRef(false);
    useEffect(() => {
        if (!props.commonApp && isInitialConfiguration && initialPrerequisiteComplete && !prevInitialPrerequisiteCompleteRef.current) {
            setTimeout(() => {
                scrollToWithOffset(environmentRef.current);
            }, 50);
        }
        prevInitialPrerequisiteCompleteRef.current = initialPrerequisiteComplete;
    }, [initialPrerequisiteComplete]);

    // Progressive disclosure scroll: when the first location name is entered, scroll to
    // Location-Specific Configuration (initial config only). Triggered on blur, not onChange.
    const [locationNameBlurred, setLocationNameBlurred] = useState(false);
    const locationNameEntered = locationNameBlurred && props.values.locationNames.some(n => n.trim().length > 0);
    const prevLocationNameEnteredRef = useRef(false);
    useEffect(() => {
        prevLocationNameEnteredRef.current = locationNameEntered;
    }, [locationNameEntered]);


    const createFilteredSections = (search: string) => {
        if (search?.length) {
            const searchLower = search.toLowerCase();
            const searchFilteredSections = allSectionsRef.current
                .map((section) => {
                    const sectionMatches = section.title.toLowerCase().includes(searchLower);
                    const filteredFields = sectionMatches
                        ? section.templateFormFields
                        : section.templateFormFields.filter(
                            (formField) => formField.title.toLowerCase().includes(searchLower)
                        );
                    return { ...section, templateFormFields: filteredFields };
                })
                .filter((section) => section.templateFormFields.length > 0);

            setFilteredSections(searchFilteredSections);
        } else {
            setFilteredSections(allSectionsRef.current);
        }
    };

    const handleTextFieldChange = (e: ChangeEvent<HTMLInputElement>, fieldName: string) => {
        return props.setFieldValue(fieldName, e.target.value);
    };

    const handleAssistantTypeChange = (selectedOption: any, value: string) => {
        // If user manually changes assistant type and there are no existing locationAppFormFields,
        // we can clear the selected form fields to start fresh
        const shouldClearSelections = props.locationAppFormFields.length === 0 && props.values.selectedFormFields.length > 0;

        props.setFieldValue("assistantType", value);

        if (shouldClearSelections) {
            props.setFieldValue("selectedFormFields", []);
        }

        // Clear PMS Name if changing away from dental scheduling
        if (value !== 'dental-scheduling') {
            props.setFieldValue("pmsName", undefined);
        }

        // Clear AI Ordering fields if changing away from ai-ordering
        if (value !== 'ai-ordering') {
            props.setFieldValue("posNameOrdering", undefined);
            props.setFieldValue("storeIds", {});
        }

        // Reset per-field autofill prompts to defaults for the newly selected assistant type
        props.setFieldValue("autofillPrompts", getDefaultAutofillPrompts(value));

        setTimeout(() => {
            scrollToWithOffset(posNameRef.current);
        }, 50);
    };

    const SCROLL_OFFSET = 72; // 56px tab bar + 16px breathing room

    const scrollToWithOffset = (el: HTMLElement | null) => {
        if (!el) return;
        // Walk up to find the nearest scrollable ancestor
        let parent = el.parentElement;
        while (parent) {
            const overflow = window.getComputedStyle(parent).overflowY;
            if (overflow === 'auto' || overflow === 'scroll') break;
            parent = parent.parentElement;
        }
        const container = parent ?? window.document.documentElement;
        const elTop = el.getBoundingClientRect().top;
        const containerTop = container instanceof HTMLElement ? container.getBoundingClientRect().top : 0;
        const currentScroll = container instanceof HTMLElement ? container.scrollTop : window.scrollY;
        container.scrollTo({ top: currentScroll + elTop - containerTop - SCROLL_OFFSET, behavior: 'smooth' });
    };

    const handlePmsNameChange = (selectedOption: any, value: string) => {
        props.setFieldValue("pmsName", value);
    };

    const handlePosNameOrderingChange = (selectedOption: any, value: string) => {
        props.setFieldValue("posNameOrdering", value);
    };

    const handleAssistantSetupTypeChange = (selectedOption: any, value: string) => {
        props.setFieldValue("assistantSetupType", value);
        // When switching to Production, clear sandbox-populated store IDs so they don't carry over.
        // When switching to Sandbox, the useEffect hooks will re-populate store IDs based on the
        // selected PMS/POS name.
        if (value === 'standard') {
            props.setFieldValue("storeIds", {});
        }
        setTimeout(() => {
            scrollToWithOffset(autogenerateRowRef.current);
        }, 50);
    };

    const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>, commonAppFormField: TemplateFormFieldModel) => {
        const selectedFormFieldsCopy = [...props.values.selectedFormFields];
        if (e.target.checked) {
            // add form field to selected form fields if not already there
            if (
                selectedFormFieldsCopy.some(
                    (selectedFormField) => selectedFormField.id === commonAppFormField.id
                )
            ) {
                // do nothing if already checked
                return;
            } else {
                // Look in both filtered sections and original common app sections to handle all cases
                const filteredFormFields = filteredSections.flatMap(
                    (section) => section.templateFormFields
                );
                const commonAppFormFields = props.commonAppSections.flatMap(
                    (section) => section.templateFormFields
                );

                // Try to find the field in filtered sections first, then fall back to common app sections
                let selectedFormFieldToCheck = filteredFormFields.find(
                    (formField) => formField.id === commonAppFormField.id || formField.title === commonAppFormField.title
                );

                if (!selectedFormFieldToCheck) {
                    selectedFormFieldToCheck = commonAppFormFields.find(
                        (formField) => formField.id === commonAppFormField.id || formField.title === commonAppFormField.title
                    );
                }

                if (selectedFormFieldToCheck) {
                    selectedFormFieldsCopy.push(selectedFormFieldToCheck);
                }
            }
        } else {
            // remove form field from selected form fields if it is there
            const selectedFormFieldToUncheck = selectedFormFieldsCopy.find(
                (selectedFormField) => selectedFormField.id === commonAppFormField.id || selectedFormField.title === commonAppFormField.title
            );

            if (selectedFormFieldToUncheck) {
                selectedFormFieldsCopy.splice(
                    selectedFormFieldsCopy.indexOf(selectedFormFieldToUncheck),
                    1
                );
            }
        }
        props.setFieldValue("selectedFormFields", selectedFormFieldsCopy);
    };

    const renderSectionFormFields = (commonAppSection: TemplateFormSectionModel) => {
        if (commonAppSection.templateFormFields.length > 0) {
            return commonAppSection?.templateFormFields?.map((commonAppFormField) => {
                return (
                    <CheckboxField
                        label={commonAppFormField.title}
                        key={commonAppFormField.id}
                        name={commonAppFormField.title}
                        checked={props.values.selectedFormFields.some(
                            (selectedFormField) =>
                                selectedFormField.title === commonAppFormField.title ||
                                selectedFormField.id === commonAppFormField.id
                        )} //checking both titles and ids for flexibility
                        onChange={(e) => handleCheckboxChange(e, commonAppFormField)}
                        className={checkboxField}
                        disabled={props.isLoadingTemplates}
                        tooltip={commonAppFormField?.tip}
                        tooltipIcon={infoIcon}
                        tooltipPosition={"right"}
                        tooltipStyle={tooltipStyle}
                    />
                );
            });
        }
    };

    const handleNextButtonClick = () => {
        if (activeIndex < filteredSections.length - 1) {
            setActiveIndex(activeIndex + 1);
        }
    };

    const handlePreviousButtonClick = () => {
        if (activeIndex <= filteredSections.length - 1) {
            setActiveIndex(activeIndex - 1);
        }
    };

    const handleSectionDropdownChange = (_fieldName: string, value: string) => {
        setActiveIndex(parseInt(value, 10));
    };

    const handleSubmitButtonClick = () => {
        // Clear previous errors and success status
        setSubmissionErrors([]);
        setSubmissionSuccess(false);
        setIsSubmitted(true);
        // Mark all fields touched so Formik inline errors become visible
        const touchedLocationNames = props.values.locationNames.reduce((acc, _, i) => ({ ...acc, [i]: true }), {});
        const touchedStoreIds = Object.keys(props.values.storeIds ?? {}).reduce((acc, k) => ({ ...acc, [k]: true }), {});
        props.setTouched({
            assistantType: true,
            assistantSetupType: true,
            pmsName: true,
            posNameOrdering: true,
            locationNames: touchedLocationNames as any,
            storeIds: touchedStoreIds as any,
        });
    };

    const handleFormSubmissionResult = (success: boolean, errors: string[] = []) => {
        if (success) {
            // Only set the onboarding cue on initial creation (Submit All), not on subsequent updates.
            // isInitialConfiguration mirrors the condition used to show the "Submit All" button label.
            const isInitialConfiguration = !props.commonApp && (!props.locationApps || props.locationApps.length === 0);
            if (isInitialConfiguration) {
                localStorage.setItem(sparkOnboardingCueKey, JSON.stringify({
                    organizationId: props.orgContainer.state.currentOrganization.id,
                    stage: 'shared-config'
                }));
            }
            setSubmissionSuccess(true);
            setSubmissionErrors([]);

            // Map autofill results to FieldGenerateResult shape for the modal
            const successes: FieldGenerateResult[] = (props.status?.autofillSuccesses ?? [])
                .map((s: { fieldLabel: string; generatedValue: string; appName?: string }) => ({ fieldTitle: s.fieldLabel, generatedValue: s.generatedValue, appName: s.appName }));
            const failures: FieldGenerateResult[] = (props.status?.autofillErrors ?? [])
                .map((e: { fieldLabel: string; error: string; appName?: string; retryAttempted?: boolean }) => ({ fieldTitle: e.fieldLabel, generatedValue: 'Unknown', appName: e.appName, retryAttempted: e.retryAttempted }));

            if (successes.length > 0 || failures.length > 0) {
                // Show the results modal — redirect happens when the user clicks OK
                setAutofillSuccessFields(successes);
                setAutofillFailedFields(failures);
                setShowAutofillResultsModal(true);
            } else {
                // Autofill did not run — use the existing 1.5s redirect
                setTimeout(() => {
                    props.history.push("/", {
                        autofillErrors: [],
                        newlySharedFieldTitles: props.status?.newlySharedFieldTitles ?? [],
                        unsyncedLocations: props.status?.unsyncedLocations ?? [],
                        posConfigErrors: props.status?.posConfigErrors ?? [],
                        refresh: true,
                    });
                }, 1500);
            }
        } else {
            setSubmissionSuccess(false);
            setIsSubmitted(false);
            setSubmissionErrors(errors.length > 0 ? errors : ["An unexpected error occurred during form submission."]);
        }
    };

    const handleAutofillResultsConfirm = () => {
        setShowAutofillResultsModal(false);
        props.history.push("/", {
            autofillErrors: props.status?.autofillErrors ?? [],
            newlySharedFieldTitles: props.status?.newlySharedFieldTitles ?? [],
            unsyncedLocations: props.status?.unsyncedLocations ?? [],
            posConfigErrors: props.status?.posConfigErrors ?? [],
            refresh: true,
        });
    };

    const handleSearch = (searchEvent: React.ChangeEvent<HTMLInputElement>) => {
        clearTimeout(searchTimer.current);
        setSearch(searchEvent.target.value);
        searchTimer.current = setTimeout(() => {
            createFilteredSections(searchEvent.target.value);
            setActiveIndex(0);
        }, WAIT_INTERVAL);
    };

    const clearSearchTerm = () => {
        setSearch("");
        setActiveIndex(0);
        setFilteredSections(allSectionsRef.current);
    };

    const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Escape") {
            clearSearchTerm();
        }
    };

    const handleStartEditAppName = (appId: string, currentName: string) => {
        setEditingAppId(appId);
        setEditingAppName(currentName);
        setAppNameSaveError(null);
    };

    const handleCancelEditAppName = () => {
        setEditingAppId(null);
        setEditingAppName("");
        setAppNameSaveError(null);
    };

    const handleSaveAppName = async (app: ApplicationModel) => {
        const trimmed = editingAppName.trim();
        if (!trimmed) {
            setAppNameSaveError("App name cannot be empty.");
            return;
        }
        if (trimmed === app.name) {
            handleCancelEditAppName();
            return;
        }
        setIsSavingAppName(true);
        setAppNameSaveError(null);
        try {
            const result = await updateApplication(app.id, {
                name: trimmed,
                shortDescription: app.shortDescription,
                description: app.description,
                keywords: app.keywords,
                invocationPhrase: app.invocationPhrase,
                imageItemId: app.imageItemId,
                featureFlags: app.featureFlags,
                posName: app.posName,
                pmsName: app.pmsName,
                posLocationId: app.posLocationId,
                pmsLocationId: app.pmsLocationId,
                isLive: app.isLive,
            });
            if (result.resultType === "Ok") {
                setLocalAppNames(prev => ({ ...prev, [app.id]: trimmed }));
                // If this is a location app, update the Formik locationNames so the row reflects the new name
                const locationIndex = props.values.locationNames.indexOf(app.name);
                if (locationIndex !== -1) {
                    const updatedNames = [...props.values.locationNames];
                    updatedNames[locationIndex] = trimmed;
                    props.setFieldValue("locationNames", updatedNames);
                }
                setEditingAppId(null);
                setEditingAppName("");
            } else {
                setAppNameSaveError(result.errors?.join(", ") || "Failed to update app name.");
            }
        } catch (e) {
            setAppNameSaveError("An unexpected error occurred.");
        } finally {
            setIsSavingAppName(false);
        }
    };

    const handleAppNameEditKeyDown = (e: KeyboardEvent<HTMLInputElement>, app: ApplicationModel) => {
        if (e.key === "Enter") {
            handleSaveAppName(app);
        } else if (e.key === "Escape") {
            handleCancelEditAppName();
        }
    };

    const renderLocationButtons = (index: number) => {
        const sectionOptions = filteredSections.map((section, i) => ({
            label: section.title,
            value: String(i),
        }));

        return (
            <SectionHeaderRow>
                {filteredSections.length >= 1 && (
                    <SectionDropdownWrapper>
                        <StyledDropdown
                            fieldName="sectionNav"
                            options={sectionOptions}
                            value={String(index)}
                            handleOnChange={handleSectionDropdownChange}
                            placeholder="Jump to section..."
                            searchable={true}
                            openMenuAbove={false}
                            size="medium"
                            disabled={!!search}
                        />
                    </SectionDropdownWrapper>
                )}

                <SearchButtonsWrapper>
                    <StyledSearchField
                        placeholder={"Search All Form Fields"}
                        boxType={"rounded"}
                        onChange={handleSearch}
                        onKeyDown={handleSearchKeyDown}
                        searchIcon={searchIcon}
                        value={search}
                        deletePosition={"right"}
                        deleteIcon={xIcon}
                        handleClear={clearSearchTerm}
                    />

                    {index !== filteredSections.length - 1 ? (
                        <ButtonWrapper>
                            <StyledButton
                                className={index === 0 && lightButtonStyle}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    (index !== 0 ? handlePreviousButtonClick() : e.stopPropagation())
                                }
                                }
                                themes={["icon"]}
                                icon={index !== 0 ? leftArrow : lightLeftArrow}
                                disabled={index === 0}
                            />
                            <StyledButton
                                onClick={(e) => { handleNextButtonClick(); e.stopPropagation(); }}
                                themes={["icon"]}
                                icon={filteredSections.length > 0 ? rightArrow : lightRightArrow}
                                disabled={filteredSections.length === 0}
                            />
                        </ButtonWrapper>
                    ) : (
                        <ButtonWrapper>
                            <StyledButton
                                className={filteredSections.length === 1 && lightButtonStyle}
                                onClick={(e) => {
                                    filteredSections.length !== 1
                                        ? handlePreviousButtonClick()
                                        : null
                                    e.stopPropagation();
                                }}
                                themes={["icon"]}
                                icon={filteredSections.length !== 1 ? leftArrow : lightLeftArrow}
                                disabled={filteredSections.length === 1}
                            />
                            <StyledButton
                                className={lightButtonStyle}
                                onClick={(e) => e.stopPropagation()}
                                disabled={true}
                                themes={["icon"]}
                                icon={lightRightArrow}
                            />
                        </ButtonWrapper>
                    )}
                </SearchButtonsWrapper>
            </SectionHeaderRow>
        );
    };

    const submitForm = async (skipPOSConfigLocationIndexes: number[] = []) => {
        await props.setFieldValue('skipPOSConfigLocationIndexes', skipPOSConfigLocationIndexes, false);
        handleSubmitButtonClick();
        props.handleSubmit();
    };

    const findExistingPOSConfigConflicts = async (): Promise<{
        conflicts: ExistingPOSConfigConflict[];
        verificationErrors: string[];
    }> => {
        if (props.values.assistantType !== 'ai-ordering' || props.values.assistantSetupType === 'out-of-the-box') {
            return { conflicts: [], verificationErrors: [] };
        }

        const conflicts: ExistingPOSConfigConflict[] = [];
        const verificationErrors: string[] = [];

        for (let locationIndex = 0; locationIndex < props.values.locationNames.length; locationIndex++) {
            const locationName = props.values.locationNames[locationIndex]?.trim();
            const storeId = props.values.storeIds?.[locationIndex]?.trim();
            const locationId = props.locationApps[locationIndex]?.id;
            const isNewLocation = !props.locationApps?.some(app => app.name === locationName);
            if (!locationName || !storeId || !isNewLocation || !locationId) continue;
            if (isSandboxConfiguring(props.values.assistantSetupType, storeId, props.values.posNameOrdering)) continue;

            try {
                const configResult = await loadPOSConfigForLocationId(locationId);
                if (configResult.resultType === 'Ok' && !!configResult.data?.data?.trim()) {
                    conflicts.push({ locationIndex, locationName, storeId });
                    continue;
                }

                if (configResult.resultType !== 'Ok'
                    && configResult.resultType !== 'Invalid'
                    && configResult.resultType !== 'NotFound') {
                    verificationErrors.push(
                        `Could not verify the saved POS configuration for Store ID "${storeId}". No apps were created. Please try again.`
                    );
                }
            } catch {
                verificationErrors.push(
                    `Could not verify the saved POS configuration for Store ID "${storeId}". No apps were created. Please try again.`
                );
            }
        }

        return {
            conflicts,
            verificationErrors: Array.from(new Set(verificationErrors)),
        };
    };

    const handleSubmitRequest = async () => {
        setSubmissionErrors([]);
        const errors = await props.validateForm();
        const currentValues = props.values;
        const isUrlAssistant = currentValues.assistantType === 'ai-ordering' || currentValues.assistantType === 'dental-scheduling';
        const orgUrlVal = currentValues.websiteUrl ?? '';
        const orgUrlInvalid = !props.commonApp && isUrlAssistant && !!orgUrlVal && !/^https?:\/\/.+\..+/.test(orgUrlVal.trim());
        const locationUrls = Object.values(currentValues.locationWebsiteUrls ?? {}) as string[];
        const anyLocationUrlInvalid = !props.commonApp && isUrlAssistant && locationUrls.some(
            (url) => !!url && !/^https?:\/\/.+\..+/.test(url.trim())
        );

        if (Object.keys(errors).length > 0 || orgUrlInvalid || anyLocationUrlInvalid) {
            handleSubmitButtonClick();
            requestAnimationFrame(() => {
                const firstError = document.querySelector('[data-validation-error]');
                scrollToWithOffset(firstError as HTMLElement | null);
            });
            return;
        }

        setIsCheckingPOSConfigs(true);
        try {
            const { conflicts, verificationErrors } = await findExistingPOSConfigConflicts();
            if (verificationErrors.length > 0) {
                setSubmissionErrors(verificationErrors);
            } else if (conflicts.length > 0) {
                setExistingPOSConfigConflicts(conflicts);
            } else {
                await submitForm();
            }
        } finally {
            setIsCheckingPOSConfigs(false);
        }
    };

    const renderSubmitButton = () => {
        // Default state: show Submit All / Update All button
        return (
            <SubmitButtonWrapper>
                {submissionErrors.length > 0 && (
                    <PageError errors={submissionErrors} onClear={() => setSubmissionErrors([])} />
                )}
                <StyledButton
                    onClick={handleSubmitRequest}
                    loading={props.isSubmittingForm || isCheckingPOSConfigs}
                    disabled={isCheckingPOSConfigs}
                    themes={["primary"]}
                    text={(!props.commonApp && (!props.locationApps || props.locationApps.length === 0)) ? "Submit All" : "Update All"}
                    type="button"
                />
            </SubmitButtonWrapper>
        );
    };

    const handleAddLocationButtonClick = (arrayHelpers: FieldArrayRenderProps) => {
        arrayHelpers.push("");
        isLocationCollapsed && setIsLocationCollapsed(!isLocationCollapsed);

        // If we're in a post-success state, re-enable the form for the new location
        if (submissionSuccess) {
            setIsSubmitted(false);
            setShowCheckboxes(true);
        }

        // Pre-populate the new row's Location ID with the sandbox value if applicable
        if (
            props.values.assistantSetupType === 'out-of-the-box' &&
            props.values.assistantType === 'ai-ordering' &&
            props.values.posNameOrdering
        ) {
            const sandboxLocationId = SANDBOX_LOCATION_IDS[props.values.posNameOrdering];
            if (sandboxLocationId) {
                const newIndex = props.values.locationNames.length;
                const currentStoreIds = props.values.storeIds ?? {};
                if (!currentStoreIds[newIndex]) {
                    props.setFieldValue('storeIds', { ...currentStoreIds, [newIndex]: sandboxLocationId });
                }
            }
        }

        // Pre-populate the new row's Location ID for Dental Scheduling with Voicify Sandbox
        if (
            props.values.assistantSetupType === 'out-of-the-box' &&
            props.values.assistantType === 'dental-scheduling' &&
            props.values.pmsName
        ) {
            const dentalSandboxLocationIds: Record<string, string> = {
                'denticon': '266',
                'dentrix-ascend': '7000000000392',
            };
            const sandboxLocationId = dentalSandboxLocationIds[props.values.pmsName];
            if (sandboxLocationId) {
                const newIndex = props.values.locationNames.length;
                const currentStoreIds = props.values.storeIds ?? {};
                if (!currentStoreIds[newIndex]) {
                    props.setFieldValue('storeIds', { ...currentStoreIds, [newIndex]: sandboxLocationId });
                }
            }
        }
    };

    const handleAddLocationKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.currentTarget.blur();
        }
    };

    const renderAddButton = (arrayHelpers: FieldArrayRenderProps) => {
        const showCsvButtons = props.values.assistantType === 'ai-ordering'
            || props.values.assistantType === 'dental-scheduling'
            || props.values.assistantType === 'ai-answering';
        return (
            <AddButtonWrapper>
                {/* Hidden file input for CSV import */}
                {showCsvButtons && (
                    <input
                        ref={csvImportInputRef}
                        type="file"
                        accept=".csv"
                        style={{ display: 'none' }}
                        onChange={handleCSVImport}
                    />
                )}
                {showCsvButtons && !isAssistantTypeDisabled && (
                    <CsvTemplateButton
                        themes={["secondary-small", "end-tight"]}
                        text="Download Location Template"
                        type="button"
                        rightIcon={fileDownloadIcon}
                        onClick={downloadLocationTemplate}
                    />
                )}
                {showCsvButtons && (
                    <CsvImportButton
                        themes={["secondary-small", "end-tight"]}
                        text="Import Locations"
                        type="button"
                        rightIcon={fileUploadIcon}
                        disabled={isAssistantTypeDisabled}
                        onClick={() => csvImportInputRef.current?.click()}
                    />
                )}
                <Button
                    themes={["flat"]}
                    text="+ Add Location"
                    type="button"
                    className={addLocationName}
                    onClick={() => handleAddLocationButtonClick(arrayHelpers)}
                />
            </AddButtonWrapper>
        );
    };

    const renderNoFormsMessage = () => {
        const assistantTypeLabel = assistantTypeOptions.find(option => option.value === props.values.assistantType)?.label || 'this assistant type';

        return (
            <div className={noFormsMessageWrapper}>
                <p className={noMessageStyle}>
                    {search ?
                        `No results found for "${search}".` :
                        `No topics available for ${assistantTypeLabel}. This module may not be configured yet.`
                    }
                </p>
                {!search && (
                    <p className={xMessageStyle}>
                        Please contact support if you expected to see topics here.
                    </p>
                )}
            </div>
        );
    };

    const hasValidationErrors = Object.keys(props.errors).length > 0;

    return (
        <form ref={formRef} style={{ minHeight: 'calc(100vh - 296px)' }}>
            <AssistantTypeWrapper hasAssistantType={!!props.values.assistantType}>
                <DropdownField>
                    <CustomDropdown
                        label="Assistant Purpose"
                        labelStyle={assistantTypeLabelStyle}
                        handleOnChange={handleAssistantTypeChange}
                        options={assistantTypeOptions}
                        value={props.values.assistantType}
                        placeholder="Select assistant type"
                        size="medium"
                        disabled={props.isLoadingTemplates || isAssistantTypeDisabled || submissionSuccess}
                        tooltip={isAssistantTypeDisabled ? "Assistant types and PMS cannot be modified in this section. To make changes the user will need to speak to a member of the Voicify Engineering team who can make those changes in app.voicify." : undefined}
                        tooltipIcon={infoIcon}
                    />
                    {props.errors.assistantType && isSubmitted && (
                        <ValidationError>{props.errors.assistantType}</ValidationError>
                    )}
                </DropdownField>

                {props.values.assistantType === 'dental-scheduling' && (
                    <div ref={posNameRef}>
                        <DropdownField>
                            <CustomDropdown
                                label="PMS Name"
                                labelStyle={assistantTypeLabelStyle}
                                handleOnChange={handlePmsNameChange}
                                options={pmsNameOptions}
                                value={props.values.pmsName}
                                placeholder="Select PMS name"
                                size="medium"
                                disabled={props.isLoadingTemplates || isAssistantTypeDisabled || submissionSuccess}
                                tooltip={isAssistantTypeDisabled ? "Assistant types and PMS cannot be modified in this section. To make changes the user will need to speak to a member of the Voicify Engineering team who can make those changes in app.voicify." : undefined}
                                tooltipIcon={infoIcon}
                            />
                            {props.errors.pmsName && isSubmitted && (
                                <ValidationError>{props.errors.pmsName}</ValidationError>
                            )}
                        </DropdownField>
                    </div>
                )}

                {props.values.assistantType === 'ai-ordering' && (
                    <div ref={posNameRef}>
                        <DropdownField>
                            <CustomDropdown
                                label="System Integration"
                                labelStyle={assistantTypeLabelStyle}
                                handleOnChange={handlePosNameOrderingChange}
                                options={posNameOrderingOptions}
                                value={props.values.posNameOrdering}
                                placeholder="Select POS name"
                                size="medium"
                                disabled={props.isLoadingTemplates || isAssistantTypeDisabled || submissionSuccess}
                                tooltip={isAssistantTypeDisabled ? "Assistant types and PMS cannot be modified in this section. To make changes the user will need to speak to a member of the Voicify Engineering team who can make those changes in app.voicify." : undefined}
                                tooltipIcon={infoIcon}
                            />
                            {props.errors.posNameOrdering && isSubmitted && (
                                <ValidationError>{props.errors.posNameOrdering}</ValidationError>
                            )}
                        </DropdownField>
                    </div>
                )}

                {!props.commonApp && initialPrerequisiteComplete && (
                    <div ref={environmentRef}>
                        <DropdownField>
                            <CustomDropdown
                                label="Environment"
                                labelStyle={assistantTypeLabelStyle}
                                handleOnChange={handleAssistantSetupTypeChange}
                                options={assistantSetupTypeOptions}
                                value={props.values.assistantSetupType}
                                placeholder="Select setup type"
                                size="medium"
                                disabled={props.isLoadingTemplates || submissionSuccess}
                            />
                            {props.errors.assistantSetupType && isSubmitted && (
                                <ValidationError>{props.errors.assistantSetupType}</ValidationError>
                            )}
                        </DropdownField>
                    </div>
                )}

            </AssistantTypeWrapper>

            {props.values.assistantType && (
                <>
                    {(!props.commonApp ? !!props.values.assistantSetupType : true) && (
                        <ContentCollapsingSection
                            title="Locations"
                            icon={locationsIcon}
                            isCollapsedByDefault={false}
                            forceOpen={!isLocationCollapsed}
                            bottom
                            titleRightRowComponent={
                                <LocationsCount>{props.values.locationNames.filter(n => n.trim()).length} added</LocationsCount>
                            }
                            className={locationsSectionStyle}
                        >
                            <FieldArray
                                validateOnChange={false}
                                name="locationNames"
                                render={(arrayHelpers) => (
                                    <>
                                        <LocationsList ref={locationNamesRef}>
                                            {props.values.locationNames.map((locationName, index) => {
                                                // Match existing app only at the same index to avoid prematurely locking a
                                                // new row whose typed name happens to match a different existing location.
                                                const existingApp = (() => {
                                                    const appAtIndex = props.locationApps?.[index];
                                                    if (!appAtIndex) return undefined;
                                                    if (appAtIndex.name === locationName || localAppNames[appAtIndex.id] === locationName) {
                                                        return appAtIndex;
                                                    }
                                                    return undefined;
                                                })();
                                                const isExisting = !!existingApp || (submissionSuccess && !!locationName.trim());
                                                const needsStoreId = props.values.assistantType === 'ai-ordering' || props.values.assistantType === 'dental-scheduling';
                                                const isNewOrg = !props.commonApp;
                                                const locationNameEmpty = isSubmitted && !locationName.trim();
                                                const locationClone = !!(props.values.locationCloneEnabled?.[index]);
                                                const locationAutogenerate = needsStoreId && !!(props.values.locationAutogenerateEnabled?.[index]);
                                                const requiresLocationUrl = locationAutogenerate
                                                    || (props.values.assistantType === 'ai-ordering' && locationClone);
                                                const locationUrlVal = props.values.locationWebsiteUrls?.[index] ?? '';
                                                const locationUrlRequired = requiresLocationUrl && !locationUrlVal && isSubmitted;
                                                const locationUrlInvalid = isSubmitted && !!locationUrlVal && !/^https?:\/\/.+\..+/.test(locationUrlVal.trim());
                                                const locationCloneSourceAppId = props.values.locationCloneSourceAppIds?.[index] ?? '';
                                                const cloneRequired = locationClone && !locationCloneSourceAppId && isSubmitted;
                                                const cloneApps = (props.locationApps ?? []).filter(app => !!app.id && !!app.name);
                                                const cloneAppOptions = cloneApps.map(app => ({
                                                    value: app.id as string,
                                                    label: app.name as string
                                                }));
                                                const cloneSourceName = cloneApps.find(app => app.id === locationCloneSourceAppId)?.name || 'the selected location';
                                                const cloneTooltipText = `All form field values, except auto-generated fields listed below, will be cloned from ${cloneSourceName}.`;
                                                const rowError = locationNameEmpty
                                                    ? 'Location name is required'
                                                    : locationUrlRequired
                                                        ? 'Location website URL is required to autogenerate content'
                                                        : locationUrlInvalid
                                                            ? 'Please enter a valid website URL (e.g. https://yourbusiness.com)'
                                                            : cloneRequired
                                                                ? 'Please select a location to clone from'
                                                                : null;
                                                const isEditingThisRow = isExisting && existingApp && editingAppId === existingApp.id;
                                                return (
                                                    <LocationRowWrapper key={`locationNames.${index}`}>
                                                        <LocationRow>
                                                            <LocationRowContent>
                                                                <LocationRowInputs>
                                                                    {isEditingThisRow ? (
                                                                        <AppNameEditRow style={{ flex: 1 }}>
                                                                            <AppNameEditInput
                                                                                value={editingAppName}
                                                                                onChange={(e) => setEditingAppName(e.target.value)}
                                                                                onKeyDown={(e) => handleAppNameEditKeyDown(e, existingApp)}
                                                                                disabled={isSavingAppName}
                                                                                autoFocus
                                                                            />
                                                                            <AppNameEditAction
                                                                                src={checkIcon}
                                                                                onClick={() => handleSaveAppName(existingApp)}
                                                                                title="Save name"
                                                                            />
                                                                            <AppNameEditAction
                                                                                src={xIcon}
                                                                                onClick={handleCancelEditAppName}
                                                                                title="Cancel"
                                                                            />
                                                                        </AppNameEditRow>
                                                                    ) : (
                                                                        <>
                                                                            <LocationNameInput
                                                                                name={`locationNames.${index}`}
                                                                                disabled={isExisting}
                                                                                value={locationName}
                                                                                label=""
                                                                                placeholder="Location name"
                                                                                onChange={(e) => handleTextFieldChange(e, `locationNames.${index}`)}
                                                                                fieldContainerStyle={locationNameEmpty ? locationInputErrorContainerStyle : locationInputContainerStyle}
                                                                                className={locationInputStyle}
                                                                                autoFocus={index === props.values.locationNames.length - 1 && !isExisting && props.values.locationNames.length > 1}
                                                                                onKeyDown={(e) => handleAddLocationKeyDown(e)}
                                                                                onBlur={() => { if (locationName.trim().length > 0) setLocationNameBlurred(true); }}
                                                                                useForm={false}
                                                                            />
                                                                            {isExisting && existingApp && (
                                                                                <AppNameEditIcon
                                                                                    src={pencilIcon}
                                                                                    onClick={() => handleStartEditAppName(existingApp.id, locationName)}
                                                                                    title="Edit app name"
                                                                                />
                                                                            )}
                                                                        </>
                                                                    )}
                                                                    {!isEditingThisRow && needsStoreId && (
                                                                        <StoreIdField
                                                                            name={`storeIds.${index}`}
                                                                            disabled={isExisting}
                                                                            value={props.values.storeIds?.[index] ?? ''}
                                                                            label=""
                                                                            placeholder={isExisting ? '' : props.values.assistantType === 'dental-scheduling' ? 'Default Office ID' : 'Location ID'}
                                                                            onChange={(e) => props.setFieldValue(`storeIds.${index}`, e.target.value)}
                                                                            fieldContainerStyle={
                                                                                locationInputContainerStyle
                                                                            }
                                                                            className={locationInputStyle}
                                                                            useForm={false}
                                                                        />
                                                                    )}
                                                                    {(props.values.assistantType === 'ai-ordering' || props.values.assistantType === 'dental-scheduling') && (
                                                                        <LocationAutogenerateCheckbox>
                                                                            <CheckboxField
                                                                                label="Autogenerate"
                                                                                checked={locationAutogenerate}
                                                                                disabled={submissionSuccess}
                                                                                onChange={(e) => {
                                                                                    const checked = e.target.checked;
                                                                                    const updated = { ...(props.values.locationAutogenerateEnabled ?? {}), [index]: checked };
                                                                                    props.setFieldValue('locationAutogenerateEnabled', updated);
                                                                                    // Keep legacy derived flag in sync
                                                                                    props.setFieldValue('autogenerateEnabled', Object.values(updated).some(Boolean));
                                                                                    if (!checked && !(props.values.assistantType === 'ai-ordering' && locationClone)) {
                                                                                        // Clear this location's URL when unchecked
                                                                                        const updatedUrls = { ...(props.values.locationWebsiteUrls ?? {}) };
                                                                                        delete updatedUrls[index];
                                                                                        props.setFieldValue('locationWebsiteUrls', updatedUrls);
                                                                                    }
                                                                                }}
                                                                                className={locationAutogenerateCheckboxStyle}
                                                                            />
                                                                        </LocationAutogenerateCheckbox>
                                                                    )}
                                                                    {requiresLocationUrl && (
                                                                        <LocationUrlInputWrapper>
                                                                            <LocationWebsiteUrlField
                                                                                name={`locationWebsiteUrls.${index}`}
                                                                                disabled={false}
                                                                                value={props.values.locationWebsiteUrls?.[index] ?? ''}
                                                                                label=""
                                                                                placeholder="https://yourlocation.com"
                                                                                onChange={(e) => props.setFieldValue(`locationWebsiteUrls.${index}`, e.target.value)}
                                                                                onBlur={() => {
                                                                                    const normalized = normalizeUrl(props.values.locationWebsiteUrls?.[index] ?? '');
                                                                                    if (normalized !== (props.values.locationWebsiteUrls?.[index] ?? '')) props.setFieldValue(`locationWebsiteUrls.${index}`, normalized);
                                                                                }}
                                                                                fieldContainerStyle={(locationUrlRequired || locationUrlInvalid) ? locationInputErrorContainerStyle : locationInputContainerStyle}
                                                                                className={locationInputStyle}
                                                                                useForm={false}
                                                                            />
                                                                            <TooltipWrapper
                                                                                text={locationName.trim() ? `This will be used to generate content specific to the ${locationName.trim()} location.` : "This will be used to generate content specific to this location."}
                                                                                place="top"
                                                                                maxWidth={280}
                                                                            >
                                                                                <UrlInfoIcon src={infoIcon} alt="info" />
                                                                            </TooltipWrapper>
                                                                        </LocationUrlInputWrapper>
                                                                    )}
                                                                    {props.commonApp && !isExisting && (
                                                                        <LocationAutogenerateCheckbox>
                                                                            <CheckboxField
                                                                                label="Clone"
                                                                                checked={locationClone}
                                                                                disabled={submissionSuccess}
                                                                                tooltip={cloneTooltipText}
                                                                                tooltipIcon={infoIcon}
                                                                                tooltipStyle={tooltipStyle}
                                                                                tooltipPosition="top"
                                                                                onChange={(e) => {
                                                                                    const checked = e.target.checked;
                                                                                    const updated = { ...(props.values.locationCloneEnabled ?? {}), [index]: checked };
                                                                                    props.setFieldValue('locationCloneEnabled', updated);
                                                                                    if (!checked) {
                                                                                        const updatedSources = { ...(props.values.locationCloneSourceAppIds ?? {}) };
                                                                                        delete updatedSources[index];
                                                                                        props.setFieldValue('locationCloneSourceAppIds', updatedSources);
                                                                                        if (!locationAutogenerate) {
                                                                                            const updatedUrls = { ...(props.values.locationWebsiteUrls ?? {}) };
                                                                                            delete updatedUrls[index];
                                                                                            props.setFieldValue('locationWebsiteUrls', updatedUrls);
                                                                                        }
                                                                                    }
                                                                                }}
                                                                                className={locationAutogenerateCheckboxStyle}
                                                                            />
                                                                        </LocationAutogenerateCheckbox>
                                                                    )}
                                                                    {props.commonApp && !isExisting && locationClone && (
                                                                        <LocationCloneDropdownWrapper>
                                                                            <StyledDropdown
                                                                                fieldName={`locationCloneSourceAppIds.${index}`}
                                                                                options={cloneAppOptions}
                                                                                value={locationCloneSourceAppId}
                                                                                handleOnChange={(_, value) => {
                                                                                    const updated = { ...(props.values.locationCloneSourceAppIds ?? {}), [index]: value as string };
                                                                                    props.setFieldValue('locationCloneSourceAppIds', updated);
                                                                                }}
                                                                                placeholder="Clone from..."
                                                                                size="medium"
                                                                                disabled={submissionSuccess}
                                                                            />
                                                                        </LocationCloneDropdownWrapper>
                                                                    )}
                                                                </LocationRowInputs>
                                                                {isEditingThisRow && appNameSaveError && <AppNameError>{appNameSaveError}</AppNameError>}
                                                                {rowError && <ValidationError>{rowError}</ValidationError>}
                                                            </LocationRowContent>
                                                            {!isExisting && index !== 0 && (
                                                                <DeleteIcon
                                                                    src={deleteIcon}
                                                                    onClick={() => arrayHelpers.remove(index)}
                                                                />
                                                            )}
                                                        </LocationRow>
                                                    </LocationRowWrapper>
                                                );
                                            })}
                                        </LocationsList>
                                        {renderAddButton(arrayHelpers)}
                                    </>
                                )}
                            />
                        </ContentCollapsingSection>
                    )}

                    <OutsideFormWrapper reserveMenuSpace={!props.commonApp && !props.values.assistantSetupType}>

                        {(props.commonApp || props.values.assistantSetupType) && showCheckboxes && (props.commonApp ? true : locationNameEntered) && (!isSubmitted || hasValidationErrors) && (
                            <>
                                <LocationSpecificHeader>
                                    <CheckBoxSectionsTitle ref={locationTopicsRef}>
                                        Location-Specific Configuration
                                    </CheckBoxSectionsTitle>
                                    <RecommendedFieldsButton
                                        themes={["secondary-small"]}
                                        text={allRecommendedLocationFieldsSelected ? "Recommended selected" : "Select recommended"}
                                        type="button"
                                        disabled={props.isLoadingTemplates || availableRecommendedLocationFields.length === 0 || allRecommendedLocationFieldsSelected}
                                        onClick={handleSelectRecommendedLocationFields}
                                    />
                                </LocationSpecificHeader>
                                <CheckBoxSectionsDescription>
                                    Select the form fields that will have different values per location.
                                </CheckBoxSectionsDescription>
                                {(props.commonApp || props.locationApps?.length > 0) && (
                                    <NotesActionRow>
                                        <NotesActionCopy>Need internal notes for a specific location?</NotesActionCopy>
                                        <ViewNotesButton
                                            themes={["secondary-small", "end-tight"]}
                                            text="View location notes"
                                            type="button"
                                            onClick={() => setShowClientNotesModal(true)}
                                        />
                                    </NotesActionRow>
                                )}

                                {props.isLoadingTemplates || (filteredSections.length === 0 && !isSubmitted && !search) ? (
                                    <LoaderWrapper>
                                        <SecondaryLoader />
                                    </LoaderWrapper>
                                ) : (
                                    <FieldArray
                                        name="selectedFormFields"
                                        render={() => {
                                            return (
                                                <CheckboxFieldWrapper>
                                                    {Array.isArray(filteredSections) &&
                                                        filteredSections.length > 0 ? (
                                                        filteredSections.map((section, index) => {
                                                            if (activeIndex === index && activeIndex !== null) {
                                                                return (
                                                                    <div key={index}>
                                                                        <InsideFormWrapper>
                                                                            <SectionWrapper
                                                                                key={`${section.title}.${index}`}
                                                                            >
                                                                                <SectionTitle>
                                                                                    <InsideFormHeader>
                                                                                        {renderLocationButtons(index)}
                                                                                    </InsideFormHeader>
                                                                                </SectionTitle>
                                                                                {renderSectionFormFields(section)}
                                                                                <PageNumberWrapper>
                                                                                    <p>
                                                                                        {activeIndex + 1} /{" "}
                                                                                        {filteredSections.length}
                                                                                    </p>
                                                                                </PageNumberWrapper>
                                                                            </SectionWrapper>
                                                                        </InsideFormWrapper>
                                                                    </div>
                                                                );
                                                            }
                                                            return null;
                                                        })
                                                    ) : (
                                                        <>
                                                            <InsideFormWrapper>
                                                                <SectionWrapper key={``}>
                                                                    <SectionTitle>
                                                                        <InsideFormHeader>
                                                                            {renderNoFormsMessage()}
                                                                            {renderLocationButtons(activeIndex)}
                                                                        </InsideFormHeader>
                                                                    </SectionTitle>
                                                                </SectionWrapper>
                                                            </InsideFormWrapper>
                                                        </>
                                                    )}
                                                </CheckboxFieldWrapper>
                                            );
                                        }}
                                    />
                                )}
                            </>
                        )}          </OutsideFormWrapper>

                    {(props.values.assistantType === 'ai-ordering' || props.values.assistantType === 'dental-scheduling')
                        && (props.commonApp
                            ? true
                            : (!!props.values.assistantSetupType && locationNameEntered && Object.values(props.values.locationAutogenerateEnabled ?? {}).some(Boolean))
                        ) && (
                            <ContentCollapsingSection
                                title="Autogenerate Content"
                                icon={autoGenerateIcon}
                                isCollapsedByDefault={!!props.commonApp}
                                forceOpen={Object.values(props.values.locationAutogenerateEnabled ?? {}).some(Boolean)}
                                bottom={true}
                                className={autofillPromptSectionStyle}
                            >
                                {(() => {
                                    const orgUrlVal = props.values.websiteUrl ?? '';
                                    const websiteUrlFormatInvalid = isSubmitted && !!orgUrlVal && !/^https?:\/\/.+\..+/.test(orgUrlVal.trim());
                                    const anyAutogenerateEnabled = Object.values(props.values.locationAutogenerateEnabled ?? {}).some(Boolean);
                                    // On the update path with no location autogenerate checked and no URL yet,
                                    // show a hint directing the user to either enter a URL or check a location.
                                    const showUpdateHint = !!props.commonApp && !anyAutogenerateEnabled && !orgUrlVal;
                                    return (
                                        <>
                                            {showUpdateHint && (
                                                <DisabledAutogenerateNotice>
                                                    Enter an Organization Website URL below to autogenerate shared configuration fields, or check Autogenerate on a location above to also regenerate location-specific fields.
                                                </DisabledAutogenerateNotice>
                                            )}
                                            <WebsiteUrlFieldWrapper ref={autogenerateRowRef}>
                                                <AutogenerateUrlGroup ref={websiteUrlInputWrapperRef}>
                                                    <WebsiteUrlField
                                                        name="websiteUrl"
                                                        value={orgUrlVal}
                                                        label={
                                                            <UrlLabelRow>
                                                                <span>Organization Website URL</span>
                                                                <TooltipWrapper
                                                                    text="This will be used to generate content inside the shared configuration."
                                                                    place="top"
                                                                    maxWidth={280}
                                                                >
                                                                    <UrlInfoIcon src={infoIcon} alt="info" />
                                                                </TooltipWrapper>
                                                            </UrlLabelRow>
                                                        }
                                                        labelStyle={websiteUrlLabelStyle}
                                                        placeholder="https://yourbusiness.com"
                                                        onChange={(e) => props.setFieldValue('websiteUrl', e.target.value)}
                                                        onBlur={() => {
                                                            const normalized = normalizeUrl(props.values.websiteUrl ?? '');
                                                            if (normalized !== (props.values.websiteUrl ?? '')) props.setFieldValue('websiteUrl', normalized);
                                                        }}
                                                        fieldContainerStyle={(isSubmitted && (!orgUrlVal || websiteUrlFormatInvalid)) ? locationInputErrorContainerStyle : locationInputContainerStyle}
                                                        className={locationInputStyle}
                                                        useForm={false}
                                                        disabled={submissionSuccess}
                                                    />
                                                    {(websiteUrlFormatInvalid || (!orgUrlVal && isSubmitted)) && (
                                                        <ValidationError>
                                                            {websiteUrlFormatInvalid
                                                                ? 'Please enter a valid website URL (e.g. https://yourbusiness.com)'
                                                                : 'Organization website URL is required to autogenerate content'}
                                                        </ValidationError>
                                                    )}
                                                </AutogenerateUrlGroup>
                                            </WebsiteUrlFieldWrapper>
                                        </>
                                    );
                                })()}
                                {(() => {
                                    const anyAutogenerateEnabled = Object.values(props.values.locationAutogenerateEnabled ?? {}).some(Boolean);
                                    const orgWebsiteUrl = props.values.websiteUrl?.trim() || '';
                                    // A shared config field is enabled when a URL has been entered (no location
                                    // checkbox required). A location-specific field additionally requires at least
                                    // one location to have autogenerate checked.
                                    const sharedFieldsEnabled = !!props.commonApp ? !!orgWebsiteUrl : true;
                                    const locationFieldsEnabled = !!props.commonApp ? (anyAutogenerateEnabled && !!orgWebsiteUrl) : true;
                                    // First non-empty location URL, used as the preview URL for location-specific fields.
                                    const firstLocationUrl = Object.values(props.values.locationWebsiteUrls ?? {})
                                        .map((u) => (u ?? '').trim())
                                        .find((u) => u.length > 0) ?? '';
                                    const getUrlForVariable = (variable: string): string => {
                                        const isLocationSpecific = props.values.selectedFormFields.some(
                                            (sf) => sf.associatedVariable === variable
                                        );
                                        return isLocationSpecific
                                            ? (firstLocationUrl || orgWebsiteUrl)
                                            : orgWebsiteUrl;
                                    };
                                    const substituteUrl = (text: string, url: string) =>
                                        url ? text.replace(/\{websiteUrl\}/g, url) : text;
                                    const restoreTemplate = (text: string, url: string) =>
                                        url ? text.split(url).join('{websiteUrl}') : text;
                                    return Array.from(FORM_FILL_VARIABLES_BY_ASSISTANT_TYPE[props.values.assistantType] ?? []).map((variable) => {
                                        const urlForVariable = getUrlForVariable(variable);
                                        const isDeleted = props.values.autofillPrompts !== undefined && !(variable in props.values.autofillPrompts);
                                        const isLocationSpecific = props.values.selectedFormFields.some(
                                            (sf) => sf.associatedVariable === variable
                                        );
                                        // Disable this field if: after successful submit, OR if on the update path
                                        // and the field's enablement condition isn't met.
                                        const fieldDisabled = submissionSuccess
                                            || (isLocationSpecific ? !locationFieldsEnabled : !sharedFieldsEnabled);
                                        return (
                                            <AutofillPromptFieldWrapper key={variable} isSkipped={isDeleted}>
                                                <AutofillPromptLabelRow>
                                                    <AutofillPromptLabel isSkipped={isDeleted}>{FORM_FILL_FIELD_LABELS[variable] ?? variable}</AutofillPromptLabel>
                                                    {!submissionSuccess && (
                                                        <TooltipWrapper
                                                            text={fieldDisabled ? 'Organization Website URL is required to autogenerate content' : undefined}
                                                            place="top"
                                                        >
                                                            <span style={{ display: 'inline-flex' }}>
                                                                <AutofillToggleGroup disabled={fieldDisabled}>
                                                                    <AutofillToggleOption
                                                                        type="button"
                                                                        isActive={!isDeleted}
                                                                        onClick={() => {
                                                                            if (fieldDisabled) return;
                                                                            if (isDeleted) {
                                                                                props.setFieldValue('autofillPrompts', {
                                                                                    ...props.values.autofillPrompts,
                                                                                    [variable]: getDefaultAutofillPrompts(props.values.assistantType)[variable] ?? '',
                                                                                });
                                                                            }
                                                                        }}
                                                                    >
                                                                        Autogenerate
                                                                    </AutofillToggleOption>
                                                                    <AutofillToggleOption
                                                                        type="button"
                                                                        isActive={isDeleted}
                                                                        onClick={() => {
                                                                            if (fieldDisabled) return;
                                                                            if (!isDeleted) {
                                                                                const { [variable]: _, ...rest } = props.values.autofillPrompts ?? {};
                                                                                props.setFieldValue('autofillPrompts', rest);
                                                                            }
                                                                        }}
                                                                    >
                                                                        Skip
                                                                    </AutofillToggleOption>
                                                                </AutofillToggleGroup>
                                                            </span>
                                                        </TooltipWrapper>
                                                    )}
                                                </AutofillPromptLabelRow>
                                                {!isDeleted && (
                                                    <AutofillPromptTextareaWrapper>
                                                        <AutofillPromptTextarea
                                                            value={substituteUrl(props.values.autofillPrompts?.[variable] ?? '', urlForVariable)}
                                                            onChange={(e) => props.setFieldValue('autofillPrompts', {
                                                                ...props.values.autofillPrompts,
                                                                [variable]: restoreTemplate(e.target.value, urlForVariable),
                                                            })}
                                                            disabled={fieldDisabled}
                                                            rows={4}
                                                        />
                                                    </AutofillPromptTextareaWrapper>
                                                )}
                                            </AutofillPromptFieldWrapper>
                                        );
                                    });
                                })()}
                            </ContentCollapsingSection>
                        )}
                    {isSubmitted && !hasValidationErrors && (
                        <ThankYouWrapper>
                            {(() => {
                                const isDone = submissionSuccess;
                                const currentPhaseIndex = isDone
                                    ? SUBMISSION_PHASES.findIndex(p => p.key === 'done')
                                    : SUBMISSION_PHASES.findIndex(p => p.key === props.progressStep);
                                const activePhase = SUBMISSION_PHASES[currentPhaseIndex] ?? SUBMISSION_PHASES[0];
                                const mode = props.progressMode ?? 'creating';
                                const isLocationPhase = !isDone && activePhase?.key === 'create-locations';
                                const locationProgress = props.locationProgress ?? {};
                                const activeLocationName = props.progressLocationName ?? '';
                                const activeLocationStatus = locationProgress[activeLocationName];
                                const subStepLabel = LOCATION_SUB_STEP_LABELS[activeLocationStatus];
                                const successMessage = props.status?.message ?? 'Your assistants have been successfully configured.';
                                const stepLabel = isDone
                                    ? successMessage
                                    : (isLocationPhase && activeLocationName
                                        ? (subStepLabel && activeLocationStatus !== 'complete'
                                            ? `${subStepLabel} (${activeLocationName})`
                                            : (mode === 'updating' ? `Saving ${activeLocationName} app...` : `Creating ${activeLocationName} app...`))
                                        : (activePhase?.label(mode, props.values.assistantType) ?? 'Working...'));
                                const workingPhases = SUBMISSION_PHASES.filter(p => p.key !== 'done');
                                const stepNum = isDone ? workingPhases.length : (currentPhaseIndex >= 0 ? currentPhaseIndex + 1 : 1);
                                const stepTotal = workingPhases.length;
                                return (
                                    <SubmissionProgressWrapper ref={stepperRef}>
                                        <SubmissionProgressInner isDone={isDone}>
                                            {isDone
                                                ? <SubmissionDoneIcon src={checkIcon} />
                                                : <SubmissionStepSpinner />
                                            }
                                            <SubmissionInlineLabel isDone={isDone}>{stepLabel}</SubmissionInlineLabel>
                                            {!isDone && (
                                                <SubmissionStepCounter>{stepNum} / {stepTotal}</SubmissionStepCounter>
                                            )}
                                        </SubmissionProgressInner>
                                    </SubmissionProgressWrapper>
                                );
                            })()}
                        </ThankYouWrapper>
                    )}
                    {(!isSubmitted || hasValidationErrors) && (props.commonApp || locationNameEntered) && (
                        <SubmitButtonOuterWrapper>
                            {renderSubmitButton()}
                        </SubmitButtonOuterWrapper>
                    )}
                </>
            )}
            <AutoGenerateResultsModal
                showModal={showAutofillResultsModal}
                onConfirm={handleAutofillResultsConfirm}
                successFields={autofillSuccessFields}
                failedFields={autofillFailedFields}
                hasApiError={false}
                apiErrors={[]}
                isLoading={false}
            />
            {existingPOSConfigConflicts.length > 0 && (
                <ConfirmationDialog
                    title="Store ID already has a POS configuration"
                    deleteText="Yes, continue"
                    warning={true}
                    onClose={() => setExistingPOSConfigConflicts([])}
                    onConfirm={async () => {
                        const locationIndexes = Array.from(new Set(existingPOSConfigConflicts.map(conflict => conflict.locationIndex)));
                        setExistingPOSConfigConflicts([]);
                        await submitForm(locationIndexes);
                    }}
                >
                    <POSConfigWarningContent>
                        {existingPOSConfigConflicts.map(conflict => (
                            <p key={`${conflict.locationIndex}-${conflict.storeId}`}>
                                Store ID <strong>{conflict.storeId}</strong> entered for <strong>{conflict.locationName}</strong> already has a POS configuration saved.
                            </p>
                        ))}
                        <p>
                            Are you sure this is correct? Continuing will create the app and complete the remaining setup, but it will skip POS configuration so the existing saved config is not changed.
                        </p>
                    </POSConfigWarningContent>
                </ConfirmationDialog>
            )}
            {showClientNotesModal && (
                <NotesModalOverlay
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="spark-client-notes-title"
                    onClick={() => setShowClientNotesModal(false)}
                >
                    <NotesModalPanel onClick={(event) => event.stopPropagation()}>
                        <NotesModalHeader>
                            <NotesModalHeadingGroup>
                                <NotesModalTitle id="spark-client-notes-title">Location notes</NotesModalTitle>
                                <NotesModalDescription>Choose a location, then view or update its internal support notes.</NotesModalDescription>
                            </NotesModalHeadingGroup>
                            <NotesModalCloseButton
                                type="button"
                                aria-label="Close assistant notes"
                                onClick={() => setShowClientNotesModal(false)}
                            >
                                <img src={xIcon} alt="" />
                            </NotesModalCloseButton>
                        </NotesModalHeader>
                        <NotesModalBody>
                            <SparkApplicationClientNotesEditor
                                appContainer={props.appContainer}
                                commonApp={props.commonApp}
                                locationApps={props.locationApps}
                            />
                        </NotesModalBody>
                    </NotesModalPanel>
                </NotesModalOverlay>
            )}
        </form>
    );
};

const POSConfigWarningContent = styled.div`
  color: ${color_shades_darkest};
  font-family: Muli, sans-serif;
  font-size: 15px;
  line-height: 1.5;
  padding: 24px;

  p {
    margin: 0 0 16px;
  }
`;

const assistantTypeLabelStyle = css`
    font-family: Muli;
    font-size: 14px;
    color: ${dark_grey_blue};
    font-weight: 600;
    margin: 4px 0 4px 0;
`;

const websiteUrlLabelStyle = css`
    font-family: Muli;
    font-size: 14px;
    color: ${dark_grey_blue};
    font-weight: 600;
    margin: 4px 0 6px 0;
`;

const tooltipStyle = css`
  margin-left: 8px;
`;

const separatorStyle = css`
  width: 1px;
  height: 30px;
  color: ${silver_two};
  ${breakpoint_small} {
    display: none;
  }
`;

const CheckBoxSectionsDescription = styled.p`
  font-family: Muli;
  font-size: 14px;
  color: ${color_shades_darker};
  margin: 0 0 16px 21px;
  ${breakpoint_small} {
    margin: 0 12px 12px 12px;
  }
`;

const OutsideFormWrapper = styled.div<{ reserveMenuSpace?: boolean }>`
  display: flex;
  flex-direction: column;
  margin-top: 24px;
  padding-bottom: ${props => props.reserveMenuSpace ? '120px' : '24px'};
`;

const InsideFormHeader = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  padding: 0 0 20px 0;
  ${breakpoint_small} {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    padding: 0 0 8px 0;
  }
`;

const SearchButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  img {
    margin: 0px;
  }
  ${breakpoint_small} {
    width: 100%;
    margin-left: 0;
  }
`;

const SectionDropdownWrapper = styled.div`
  width: 280px;
  flex-shrink: 0;
  /* force StyledDropdown's wrapper and react-select to fill the container */
  > div,
  > div .Select {
    width: 100%;
  }
  /* hide the empty shell react-select renders — menu content is in the portal */
  .Select-menu-outer {
    display: none;
  }
  ${breakpoint_small} {
    width: 100%;
  }
`;

const SectionHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
  ${breakpoint_small} {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
`;

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
`;

const OutsideFormHeader = css`
  margin-left: 8px;
`;

const SectionTitle = styled.div`
  font-family: Muli;
  font-size: 14px;
  margin-top: -4px;
  margin-bottom: 0px;
  width: 100%;
  box-sizing: border-box;
`;

const CheckBoxSectionsTitle = styled.div`
  font-family: Muli;
  font-size: 14px;
  font-weight: 600;
  margin: 0;
`;

const LocationSpecificHeader = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin: 0 21px 8px 21px;

  ${breakpoint_small} {
    align-items: stretch;
    flex-direction: column;
    margin: 0 12px 8px 12px;
  }
`;

const RecommendedFieldsButton = styled(Button)`
  flex-shrink: 0;
  font-size: 13px;
  height: 36px;
  margin: 0;
  padding: 0 14px;

  ${breakpoint_small} {
    align-self: flex-start;
  }
`;

const CheckboxFieldWrapper = styled.div`
  padding: 0 21px;
  ${breakpoint_small} {
    padding: 8px;
  }
`;

const checkboxField = css`
  .check-label {
    font-weight: 500;
  }
  margin-left: 16px;
  ${breakpoint_small} {
    margin-left: 8px;
  }
`;

const DeleteIcon = styled.img`
  cursor: pointer;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-left: 12px;
  opacity: 0.5;
  transition: opacity 0.15s;
  &:hover {
    opacity: 1;
  }
  ${breakpoint_small} {
    margin-left: 8px;
    width: 20px;
    height: 20px;
    padding: 4px;
  }
`;

const PageNumberWrapper = styled.div`
  display: flex;
  justify-content: right;
  font-size: 12px;
  color: ${silver_two};
  margin: -4px 16px 16px 0px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: right;
  gap: 4px;
  margin-top: 0px;
  margin-left: 8px;
  padding-bottom: 0px;
`;

const SubmitButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin: 16px 0 0 0;
`;

const SubmitButtonOuterWrapper = styled.div`
  padding: 0 21px 24px;
  ${breakpoint_small} {
    padding: 0 12px 16px;
  }
`;

const InsideFormWrapper = styled.div`
  background: white;
  border: 1px solid ${color_shades_dark};
  box-sizing: border-box;
  border-radius: 8px;
  padding: 24px 12px 0px 16px;
  margin: 0px 0px 0px 0px;
  ${breakpoint_small} {
    padding: 16px 10px 0 10px;
  }
`;

const ThankYouWrapper = styled.div`
  padding: 0 21px 24px;
  .form-submission-error {
    position: relative;
    right: auto;
    bottom: auto;
    width: 100%;
    margin-top: 16px;
    border-radius: 8px;
  }
  ${breakpoint_small} {
    padding: 0 12px 16px;
  }
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80px;
  margin: 16px 0;
  
  .loader .spinner > div {
    background-color: ${cool_grey};
  }
`;

const lightButtonStyle = css`
  cursor: not-allowed;
`;

const addLocationName = css`
    color: ${text_blue};
    font-size: 14px;
    box-shadow: none;
    padding: 0 8px;
    margin: 0;
`;

const noFormsMessageWrapper = css`
  display: flex;
  flex-direction: column;
`;

const noMessageStyle = css`
  font-size: 14px;
  padding-top: 2px;
`;

const xMessageStyle = css`
  padding-top: 6px;
  color: ${color_shades_dark};
  font-style: italic;
`;

const AddButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 21px 12px 21px;
  border-bottom: 1px solid ${color_shades_dark};
  ${breakpoint_small} {
    flex-direction: column;
    align-items: stretch;
    padding: 12px;
    gap: 8px;
  }
`;

// Button that triggers the CSV file input for bulk location import (AI Ordering)
const CsvImportButton = styled(Button)`
  font-size: 14px;
  margin: 0 8px 0 0;
  .right-img {
    margin-left: 8px;
    width: 16px;
  }
  ${breakpoint_small} {
    margin: 0;
    width: 100%;
    justify-content: center;
  }
`;

// Button that downloads a blank location CSV template (AI Ordering)
const CsvTemplateButton = styled(Button)`
  font-size: 14px;
  margin: 0 8px 0 0;
  .right-img {
    margin-left: 8px;
    width: 16px;
  }
  ${breakpoint_small} {
    margin: 0;
    width: 100%;
    justify-content: center;
  }
`;

const ViewNotesButton = styled(Button)`
  font-size: 14px;
  margin: 0;

  ${breakpoint_small} {
    margin: 0;
    width: 100%;
    justify-content: center;
  }
`;

const NotesActionRow = styled.div`
  align-items: center;
  background: ${color_shades_lightest};
  border: 1px solid ${color_shades_light};
  border-radius: 10px;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin: 12px 21px 16px 21px;
  padding: 12px 14px;

  ${breakpoint_small} {
    align-items: stretch;
    flex-direction: column;
    margin: 12px 12px 16px 12px;
  }
`;

const NotesActionCopy = styled.div`
  color: ${dark_grey_blue};
  font-family: Muli, sans-serif;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.35;
`;

const NotesModalOverlay = styled.div`
  align-items: center;
  background: rgba(18, 32, 52, 0.42);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  padding: 24px;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 3000;

  ${breakpoint_small} {
    align-items: flex-end;
    padding: 0;
  }
`;

const NotesModalPanel = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 24px 80px rgba(18, 32, 52, 0.24);
  max-height: calc(100vh - 48px);
  overflow: auto;
  width: min(640px, calc(100vw - 48px));

  ${breakpoint_small} {
    border-radius: 18px 18px 0 0;
    max-height: calc(100vh - 24px);
    width: 100%;
  }
`;

const NotesModalHeader = styled.div`
  align-items: flex-start;
  border-bottom: 1px solid ${color_shades_light};
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 22px 24px 18px;
`;

const NotesModalHeadingGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const NotesModalTitle = styled.h2`
  color: ${dark_grey_blue};
  font-family: Muli, sans-serif;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.2;
  margin: 0;
`;

const NotesModalDescription = styled.div`
  color: ${cool_grey};
  font-family: Muli, sans-serif;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
`;

const NotesModalCloseButton = styled.button`
  align-items: center;
  background: ${pale_grey};
  border: 0;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  flex: 0 0 auto;
  height: 34px;
  justify-content: center;
  padding: 0;
  width: 34px;

  img {
    height: 12px;
    width: 12px;
  }
`;

const NotesModalBody = styled.div`
  padding: 22px 24px 24px;
`;


const StyledButton = styled(Button)`
  margin: 0;
`;

// Store ID text field — rendered inline next to the location name for AI Ordering
const StoreIdField = styled(TextField)`
  max-width: 200px;
  margin-left: 8px;
  ${breakpoint_small} {
    max-width: none;
    margin-left: 0;
    width: 100%;
  }
`;

const StyledSearchField = styled(SearchField)`
  && {
    padding: 0;
    margin-bottom: 0;
    flex: 1;
  }
  .field-container {
    height: 44px;
    border-radius: 8px;
    border: solid 1px ${color_shades_dark};
    box-shadow: 0px 1px 2px rgba(50, 70, 97, 0.1);
    margin-top: 0;
    img {
      margin-left: 8px;
    }
  }
  input {
    font-family: Muli, sans-serif;
    color: ${color_shades_darkest};
    font-size: 13px;
    font-weight: normal;
    letter-spacing: normal;
    border-radius: 8px;
    ::placeholder {
      font-family: Muli, sans-serif;
      font-size: 13px;
      font-weight: normal;
      font-style: normal;
      letter-spacing: normal;
      color: ${silver_two};
    }
  }
`;

const CustomDropdown = styled(StyledDropdown)`
    .Select-menu-outer {
        max-height: none !important;
        z-index: 10001;
        padding: 10px 0px;
    }
    
    .Select-menu {
        max-height: 300px;
    }
`;

const DropdownField = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 5px;
    margin-bottom: 24px;
`;

const AssistantTypeWrapper = styled.div<{ hasAssistantType: boolean }>`
    padding: 16px 16px 0 16px;
    ${breakpoint_small} {
      padding: 12px 12px 0 12px;
    }
`;

// ── Location list and rows ──────────────────────────────────────────────────

const LocationsCount = styled.span`
  font-size: 12px;
  color: ${color_shades_darker};
  font-family: Muli;
`;

const LocationsList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 21px;
  margin-top: 28px;
  max-height: 400px;
  overflow-y: auto;
  ${breakpoint_small} {
    padding: 0 12px;
    margin-top: 16px;
  }
`;

// Wraps a location row + its optional error message
const LocationRowWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${color_shades_light};
  &:last-of-type {
    border-bottom: none;
  }
  ${breakpoint_small} {
    padding: 4px 0;
    border-bottom: 1px solid ${color_shades_light};
  }
`;

const LocationRow = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 0;
  background: white;
  transition: background 0.1s;
  &:hover {
    background: ${color_shades_lightest};
  }
  ${breakpoint_small} {
    padding: 12px 0;
  }
`;

const ValidationErrorStyle = styled.div`
  display: flex;
  align-items: center;
  font-family: Muli, "sans-serif";
  font-weight: normal;
  font-size: 14px;
  color: ${color_shades_darkest};
  background-color: ${color_variants_decline_light_1};
  border: 1px solid ${color_colors_decline};
  border-radius: 8px;
  padding: 8px 16px;
  margin: 8px 0;
  ${breakpoint_small} {
    font-size: 13px;
    padding: 8px 12px;
    word-break: break-word;
  }
`;

const ValidationErrorIcon = styled.img`
  margin-right: 8px;
  flex-shrink: 0;
`;

const ValidationError: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <ValidationErrorStyle data-validation-error>
        <ValidationErrorIcon src={alertCircleIcon} />
        {children}
    </ValidationErrorStyle>
);

const LocationRowContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const LocationRowInputs = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  gap: 8px;
  min-width: 0;
  ${breakpoint_small} {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;

const LocationNameInput = styled(TextField)`
  flex: 1;
  min-width: 0;
  margin-bottom: 0;
`;

// input container for location rows — matches dropdown border/radius/height
const locationInputContainerStyle = css`
  border: 1px solid ${color_shades_dark} !important;
  box-shadow: none !important;
  background: white !important;
  height: 48px !important;
  min-height: 48px !important;
  border-radius: 8px !important;
  margin-top: 0 !important;
`;

const locationInputErrorContainerStyle = css`
  border: 1px solid #d0021b !important;
  box-shadow: none !important;
  background: white !important;
  height: 48px !important;
  min-height: 48px !important;
  border-radius: 8px !important;
  margin-top: 0 !important;
`;

const locationInputStyle = css`
  margin-bottom: 0 !important;
`;

export const SUBMISSION_PHASES: { key: string; label: (mode: 'creating' | 'updating', assistantType?: string) => string }[] = [
    {
        key: 'setup-module',
        label: (m, type) => {
            const moduleName = assistantTypeOptions.find(o => o.value === type)?.label ?? 'module';
            return m === 'updating' ? 'Saving the Shared Configuration app with your updates...' : `Applying the ${moduleName} module...`;
        }
    },
    { key: 'configure-template', label: (m) => m === 'updating' ? 'Syncing shared configuration form fields...' : 'Creating shared configuration form fields...' },
    { key: 'create-locations', label: (m) => m === 'updating' ? 'Saving location apps...' : 'Creating location apps...' },
    { key: 'publishing', label: () => 'Refreshing dashboard...' },
    { key: 'done', label: () => 'Done' },
];

export const STEPPER_COLOR = '#0069ff';
export const STEPPER_BG = '#f5f9ff';
export const STEPPER_BORDER = '#d6e6ff';
export const STEPPER_TEXT = '#324661';
export const STEPPER_SUCCESS = '#2e7d32';
export const STEPPER_SUCCESS_BG = '#f0faf0';

export const LOCATION_SUB_STEP_LABELS: Record<string, string> = {
    'pending': 'Queued',
    'creating-app': 'Creating location app...',
    'updating-app': 'Saving location app...',
    'applying-template': 'Saving location form fields...',
    'create-manage-menu': 'Creating menu app...',
    'pull-menu': 'Importing POS menu...',
    'sync-module': 'Publishing menu to assistant...',
    'complete': 'Complete',
};

const SubmissionProgressWrapper = styled.div`
    display: flex;
    flex-direction: column;
    background: ${STEPPER_BG};
    border: 1px solid ${STEPPER_BORDER};
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 105, 255, 0.08);
    margin: 16px 0;
`;

interface SubmissionInnerProps {
    isDone: boolean;
}

const SubmissionProgressInner = styled.div<SubmissionInnerProps>`
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 20px;
    background: ${({ isDone }) => isDone ? STEPPER_SUCCESS_BG : STEPPER_BG};
    transition: background 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

const SubmissionDoneIcon = styled.img`
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    filter: brightness(0) saturate(100%) invert(38%) sepia(58%) rotate(78deg) brightness(91%) contrast(85%);
`;

interface SubmissionLabelProps {
    isDone: boolean;
}

const SubmissionInlineLabel = styled.span<SubmissionLabelProps>`
    font-family: Muli, "sans-serif";
    font-size: 15px;
    font-weight: 700;
    color: ${({ isDone }) => isDone ? STEPPER_SUCCESS : STEPPER_TEXT};
    flex: 1;
    transition: color 0.4s ease;
`;

const SubmissionStepCounter = styled.span`
    font-family: Muli, "sans-serif";
    font-size: 13px;
    font-weight: 600;
    color: ${STEPPER_COLOR};
    background: white;
    padding: 4px 10px;
    border-radius: 20px;
    border: 1px solid ${STEPPER_BORDER};
    white-space: nowrap;
    flex-shrink: 0;
`;

const SubmissionStepSpinner = styled.div`
    width: 20px;
    height: 20px;
    border: 2px solid ${STEPPER_BORDER};
    border-top: 2px solid ${STEPPER_COLOR};
    border-radius: 50%;
    animation: stepperSpin 0.8s linear infinite, stepperFadeIn 0.3s ease forwards;
    flex-shrink: 0;

    @keyframes stepperSpin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }

    @keyframes stepperFadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }
`;

const ProgressBarWrapper = styled.div`
    padding: 0 20px 20px;
`;

const ProgressBarLabel = styled.p`
    font-family: Muli, "sans-serif";
    font-size: 13px;
    font-weight: 600;
    color: ${STEPPER_TEXT};
    margin: 0 0 10px 0;
    opacity: 0.8;
`;

const ProgressBarTrack = styled.div`
    background: white;
    border: 1px solid ${STEPPER_BORDER};
    border-radius: 8px;
    height: 10px;
    width: 100%;
    overflow: hidden;
`;

interface ProgressBarFillProps {
    percent: number;
}

const ProgressBarFill = styled.div<ProgressBarFillProps>`
    background: linear-gradient(90deg, ${STEPPER_COLOR}, #4da3ff);
    height: 100%;
    width: ${({ percent }) => percent}%;
    border-radius: 8px;
    transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;

    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(
            90deg,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.3) 50%,
            rgba(255, 255, 255, 0) 100%
        );
        animation: shimmer 2s infinite;
    }

    @keyframes shimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
    }
`;

// ── App Name inline edit components ─────────────────────────────────────────


const AppNameDisplayRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const AppNameText = styled.span`
  font-family: Muli, sans-serif;
  font-size: 14px;
  color: ${color_shades_darkest};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AppNameEditIcon = styled.img`
  cursor: pointer;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  opacity: 0.5;
  transition: opacity 0.15s;
  &:hover {
    opacity: 1;
  }
`;

const AppNameEditRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  ${breakpoint_small} {
    gap: 6px;
  }
`;

const AppNameEditInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 38px;
  padding: 0 10px;
  font-family: Muli, sans-serif;
  font-size: 14px;
  color: ${color_shades_darkest};
  border: 1px solid ${color_shades_dark};
  border-radius: 8px;
  outline: none;
  box-sizing: border-box;
  &:focus {
    border-color: ${text_blue};
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AppNameEditAction = styled.img`
  cursor: pointer;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  opacity: 0.7;
  transition: opacity 0.15s;
  &:hover {
    opacity: 1;
  }
`;

const AppNameError = styled.span`
  font-family: Muli, sans-serif;
  font-size: 12px;
  color: ${color_colors_decline};
  margin-left: 4px;
  white-space: nowrap;
`;

// Org-level website URL text field — styled identically to the other dropdowns/fields
const WebsiteUrlField = styled(TextField)`
    margin-bottom: 0;
`;

// Wrapper for the autogenerate checkbox + URL field; provides scroll ref anchor and left/right padding.
const WebsiteUrlFieldWrapper = styled.div`
    padding: 0;
`;

// Per-location website URL text field — inline in the location row
const LocationWebsiteUrlField = styled(TextField)`
  max-width: 240px;
  .field-container {
    margin-top: 0;
  }
  ${breakpoint_small} {
    max-width: none;
    width: 100%;
  }
`;

// Multiline textarea for the editable autofill prompt
const AutofillPromptFieldWrapper = styled.div<{ isSkipped?: boolean }>`
  border: 1px solid ${color_shades_dark};
  border-radius: 8px;
  background: ${({ isSkipped }) => isSkipped ? color_shades_lightest : 'white'};
  margin-bottom: 8px;
  overflow: hidden;
  transition: background 0.15s ease;
  &:last-child {
    margin-bottom: 0;
  }
`;

const AutofillPromptLabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  ${breakpoint_small} {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px;
  }
`;

const AutofillPromptLabel = styled.label<{ isSkipped?: boolean }>`
  display: block;
  font-family: Muli, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${({ isSkipped }) => isSkipped ? color_shades_dark : color_shades_darker};
  transition: color 0.15s ease;
`;

const AutofillToggleGroup = styled.div<{ disabled?: boolean }>`
  display: flex;
  border: 1px solid ${color_shades_dark};
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;
  margin-left: 12px;
  ${({ disabled }) => disabled ? `
    opacity: 0.45;
    pointer-events: none;
  ` : ''}
  ${breakpoint_small} {
    margin-left: 0;
    align-self: flex-start;
  }
`;

const AutofillToggleOption = styled.button<{ isActive: boolean }>`
  background: ${({ isActive }) => isActive ? text_blue : 'white'};
  color: ${({ isActive }) => isActive ? 'white' : color_shades_darker};
  border: none;
  padding: 3px 10px;
  font-family: Muli, sans-serif;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  &:hover {
    opacity: 0.85;
  }
`;

const AutofillPromptTextareaWrapper = styled.div`
  padding: 0 14px 12px 14px;
  border-top: 1px solid ${color_shades_dark};
  ${breakpoint_small} {
    padding: 0 10px 10px 10px;
  }
`;

const AutofillPromptTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 8px 10px;
  margin-top: 10px;
  font-family: Muli, sans-serif;
  font-size: 13px;
  color: ${color_shades_darkest};
  border: 1px solid ${color_shades_dark};
  border-radius: 6px;
  resize: vertical;
  box-sizing: border-box;
  outline: none;
  &:focus {
    border-color: ${text_blue};
  }
  &:disabled {
    background: ${pale_grey};
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const autofillPromptSectionStyle = css`
  && *.card-title-container {
    padding: 24px;

    .card-hover-icon {
      display: none;
    }
  }

  && *.card-body {
    padding: 28px 21px 21px 21px;
  }

  ${breakpoint_small} {
    && *.card-title-container {
      padding: 16px 12px;
    }
    && *.card-body {
      padding: 16px 10px 12px 10px;
    }
  }
`;

const locationsSectionStyle = css`
  && *.card-title-container {
    padding: 24px;

    .card-hover-icon {
      display: none;
    }
  }

  && *.card-body {
    padding: 0;
  }

  ${breakpoint_small} {
    && *.card-title-container {
      padding: 16px 12px;
    }
  }
`;

const AutogenerateUrlGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 0;
  margin-bottom: 24px;
  max-width: 480px;
  ${breakpoint_small} {
    margin-left: 0;
    max-width: none;
  }
`;

const UrlLabelRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const UrlInfoIcon = styled.img`
  width: 16px;
  height: 16px;
  opacity: 0.55;
  cursor: pointer;
  transition: opacity 0.15s;
  flex-shrink: 0;
  vertical-align: middle;
  &:hover {
    opacity: 1;
  }
`;

const LocationUrlInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 8px;
  ${breakpoint_small} {
    margin-left: 0;
    width: 100%;
  }
`;

const LocationCloneDropdownWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 8px;
  min-width: 220px;

  > div {
    width: 100%;
  }

  ${breakpoint_small} {
    margin-left: 0;
    width: 100%;
  }
`;

// Wrapper for the per-location autogenerate checkbox — keeps it visually compact
// and inline with the rest of the row inputs.
const LocationAutogenerateCheckbox = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: 8px;
  ${breakpoint_small} {
    margin-left: 0;
    margin-top: 4px;
  }
`;

// Compact style for the per-location autogenerate checkbox label
const locationAutogenerateCheckboxStyle = css`
  label {
    font-size: 12px;
    font-weight: 600;
    color: ${color_shades_darker};
    white-space: nowrap;
    margin-bottom: 0;
  }
`;

// Hint shown inside the Autogenerate Content section on the update path when no
// location has autogenerate enabled — explains why all fields are disabled.
const DisabledAutogenerateNotice = styled.p`
  font-size: 14px;
  color: ${color_shades_dark};
  margin: 0 0 16px 0;
`;

export default SparkLocationsForm;

